import { PlayDialogDifficulty } from "./ui/dialog/play_dialog.js";
import { GlobalSettings } from "./global_settings.js";
import { TestLevelBridge } from "./debug/test_level_bridge.js";
import { StressTest } from "./debug/stress_test.js";
import { BlockShowcase } from "./debug/block_showcase.js";
import { DiamondTowerShowcase } from "./debug/diamond_tower_showcase.js";
import { JarPyramidShowcase } from "./debug/jar_pyramid_showcase.js";
import { TiltedTableShowcase } from "./debug/tilted_table_showcase.js";
import { RotatedTableShowcase } from "./debug/rotated_table_showcase.js";
import { ShelfTableShowcase } from "./debug/shelf_table_showcase.js";
import { HangingTableShowcase } from "./debug/hanging_table_showcase.js";
import { ElevatorTableShowcase } from "./debug/elevator_table_showcase.js";
import { SlideTableShowcase } from "./debug/slide_table_showcase.js";
import { TableCaseShowcase } from "./debug/table_case_showcase.js";
import { LeaningLogsShowcase } from "./debug/leaning_logs_showcase.js";
import { JarCasesShowcase } from "./debug/jar_cases_showcase.js";
import { JarSandwichShowcase } from "./debug/jar_sandwich_showcase.js";
import { JarBayShowcase } from "./debug/jar_bay_showcase.js";
import { TextureTest } from "./debug/texture_test.js";
import { TableFormationShowcase } from "./debug/table_formation_showcase.js";
import { NewBlockModelsShowcase } from "./debug/new_block_models_showcase.js";
import { BrickLevelsShowcase } from "./debug/brick_levels_showcase.js";
import { StickyMineShowcase } from "./debug/sticky_mine_showcase.js";
import { BarrierShowcase } from "./debug/barrier_showcase.js";
import { LevelPackService } from "./level_pack/level_pack_service.js";
import { MAX_TABLES } from "./level_payload.js";

export { MAX_TABLES };

// Named after the MATERIALS the art shows (same language as the balancing
// table): Cube covers the plywood/brick/colored-plywood skins via `variant`,
// Stone is the purple box2 block (ex-"MetalCube"), Metal is the steel block
// (ex-"Canned").
export enum LevelPieceType
{
  Cube = 1,
  Stone = 2,
  Jar = 3,
  Cone = 4,
  Metal = 5,
  Ice = 6,
  Tree = 7,
  Watermelon = 8,
  Plastic = 9,
  RoundMetal = 10,
  IronCan = 11,
  Column = 12,
  RoundColumn = 13,
}

export type LevelGrid = number[][];

export interface LevelPiece
{
  id: number;
  type: LevelPieceType;
  x: number;
  y: number;
  // Footprint in grid cells. Cube/Stone may use half-steps (1, 1.5, 2, 2.5, ...) for
  // finer proportions — the geometry stretch supports any length. Jar/Cone must stay whole
  // integers (fixed model variants: Cone always 1x1).
  // Jar/Metal/RoundMetal/IronCan/Ice/Tree only exist as a single-cell-wide vertical column
  // model (1-3 tall) — there is no separate "wide" art. A width>1 piece for these types is
  // the SAME height-tier model/texture rotated -90° onto its side instead (a "fallen" log/
  // can/jar), so exactly one of width/height must be 1 and the other 1-3; both >1 is invalid
  // (see validateLevelLayer).
  width: number;
  height: number;
  // Forces a specific visual variant instead of the game's normal random pick.
  // Cube: 0 = purple box, 1 = box3 (brick), 2 = green box.
  // Jar: 0-3 = jam flavor (strawberry/orange/blueberry/lemon).
  // Cone: 0-1 = original, 2 = cone_two, 3-10 = all cone_v3 colours.
  // Metal/RoundMetal: 0 = golden, 1 = red.
  // Plastic: 0-7 = red/blue/yellow/orange/green/pink/light-blue/purple.

  // Stone: ignored (only one stone skin exists).
  // Omitted => random, same as before.
  variant?: number;
  // Shifts the world position by a fraction of a cell's width/height (0 or 0.5) — for
  // staircase-style layouts. Does not change which grid[y][x] cell(s) the piece occupies.
  offsetX?: number;
  offsetY?: number;
  // Shifts the world position along the depth axis by a fraction of a layer's z-step
  // (0 or 0.5) — same staircase use-case as offsetX/offsetY, applied between layers.
  offsetZ?: number;
  // Turns the model a quarter turn about Z so a piece that stands upright by nature
  // (jar/metal/ice — a natural "up") lies on its side instead. width/height are
  // already authored in the rotated footprint (e.g. a lying 3-cell jar is width 3,
  // height 1); this flag only tells the renderer to turn the model to match.
  lying?: boolean;
  // EXPERIMENTAL, Cube only: spins the block by this many degrees on each
  // axis (X/Y/Z, camera space) before physics takes over — rotationDegZ: 45
  // turns a square block into a diamond standing on its edge in the screen
  // plane; rotationDegX/Y let it lean/tilt in 3D instead. The footprint/grid
  // cell(s) stay the piece's unrotated AABB; a rotated block visually
  // overhangs its cell. Not wired into the level editor or validated against
  // neighbours yet.
  rotationDegX?: number;
  rotationDegY?: number;
  rotationDegZ?: number;
}

/**
 * An obstacle standing IN FRONT of the table — a propeller or a swinging
 * hammer the ball has to get past on its way to the blocks (see
 * entities/barrier.ts). Not a grid piece: it never sits on the tabletop, so
 * it has no footprint in any layer and never counts towards clearing the
 * level. Positions are in grid cells, the same unit the layers are laid out
 * in, so a barrier keeps its size relative to the blocks on every device.
 */
export interface BarrierConfig
{
  /** What turns on the bearing: 0 = propeller of blades, 1 = swinging hammer. */
  head: number;
  /** What holds it up: 1 = pillar out of the sky, 2 = rod from the left edge, 3 = rod from the right edge. */
  mount: number;
  /** Across the frame from the middle of its table, + to the right. */
  x: number;
  /** Height of the bearing above the tabletop. */
  y: number;
  /** How far in front of the table its front edge stands. Defaults to 1. */
  z?: number;
  /** Turns the head about the depth axis, degrees — a hammer's rest angle. */
  angle?: number;
}

export interface LevelLayer
{
  grid: LevelGrid;
  pieces: LevelPiece[];
}

export interface TableRotationConfig
{
  angle?: number;
  spin?: number;
}

// Where this table sits within the level's formation, in normalized units:
// u across the frame (+ right), v in depth (+ towards the camera), both -1..1.
// Authored by the level editor's generator; the client turns it into metres
// using the real model sizes, so the same slot fits every device.
export interface TableSlot
{
  u: number;
  v: number;
}

export type TableShape = "rectangular" | "round" | "oval";

export interface TableTopScale
{
  width?: number;
  depth?: number;
}

export interface TableConfig
{
  layers: LevelLayer[];
  rotation?: TableRotationConfig;

  shape?: TableShape;
  hang?: boolean;
  elevator?: boolean;

  slide?: boolean;
  slot?: TableSlot;
  tiltDeg?: number;
  topScale?: TableTopScale;

  liftFrom?: number;
  standOn?: number;

  barriers?: BarrierConfig[];
}

export interface LevelConfig
{
  level: number;
  uid?: string;

  difficulty: PlayDialogDifficulty;
  layers?: LevelLayer[];
  tables?: TableConfig[];

  barriers?: BarrierConfig[];

  tiltDeg?: number;

  shape?: TableShape;

  /** Same as TableConfig.hang, for a level with no `tables` array. */
  hang?: boolean;

  /** Same as TableConfig.elevator, for a level with no `tables` array. */
  elevator?: boolean;

  /** Same as TableConfig.slide, for a level with no `tables` array. */
  slide?: boolean;

  /** Same as TableConfig.topScale, for a level with no `tables` array. */
  topScale?: TableTopScale;

  /**
   * This level is where a new block is introduced — the generator paces the
   * campaign around these. Absent on every level authored before the marker
   * existed, and on server-driven levels that simply do not start anything new.
   */
  isFeature?: boolean;
  /** Labels of the blocks that unlock exactly at this level, e.g. ["скло"]. */
  unlocks?: string[];
}

export const MAX_LEVEL_LAYERS = 10;
export const MAX_TABLE_GRID_ROWS = 10;

/** Depth of one table, in layers. Independent of how wide the table is. */
export const MAX_TABLE_LAYERS = 10;

/**
 * Widest a SINGLE table may be. This is a sanity ceiling, not a fitting rule —
 * whether a level actually fits the screen is measured properly by the field
 * layout solver, which knows the real table sizes and the real frame.
 *
 * 8 is comfortably inside what the tabletop model holds: its measured width
 * fits about 9.5 columns, so an 8-wide table still SHRINKS the model rather than
 * stretching it (see PhysicsTable#stretchTableTopWidth). The previous ceiling of
 * 5 squeezed it to 57% of its authored width, which made every table in a level
 * read the same near-square size.
 *
 * createLayeredBlocks() drops blocks past this column, so the level editor holds
 * a mirror of this number and the two must be raised together.
 */
export function maxTableGridCols(): number
{
  return 9;
}

/**
 * Columns a level may spend on ONE DEPTH BAND — the tables standing at roughly
 * the same distance from the camera.
 *
 * Per band, not per level: tables separated in depth clear each other along z
 * rather than across the frame, and on screen their widths overlap instead of
 * adding up, so bands cost the max of their widths and not the sum. Charging one
 * flat budget for the whole level made a lone table at the back subsidise the
 * row in front of it, and forced every table down to the same size.
 */
export function tableColumnBudget( table_count: number ): number
{
  return table_count <= 2 ? 10 : 14;
}

/**
 * How close two slots must sit in depth to count as one band, as a fraction of
 * the level's whole depth spread. Relative, so it reads the same whatever depth
 * stretch the editor baked into the slots. Mirrors DEPTH_BAND_TOLERANCE in the
 * editor's field_layout.
 */
const DEPTH_BAND_TOLERANCE = 0.15;

/**
 * Table indices grouped by depth, back to front. Levels authored before slots
 * existed have nothing to group on, so they come back as a single band and get
 * charged the old level-wide budget.
 */
function depthBands( tables: TableConfig[] ): number[][]
{
  const depths = tables.map( table => table.slot?.v );

  if ( depths.some( v => v === undefined ) )
    return [ tables.map( ( _, i ) => i ) ];

  const vs = depths as number[];
  const tolerance = ( Math.max( ...vs ) - Math.min( ...vs ) ) * DEPTH_BAND_TOLERANCE;
  const back_to_front = vs.map( ( _, i ) => i ).sort( ( a, b ) => vs[a] - vs[b] || a - b );

  const bands: number[][] = [];

  for ( const i of back_to_front )
  {
    const band = bands[bands.length - 1];

    if ( band !== undefined && vs[i] - vs[band[band.length - 1]] <= tolerance )
      band.push( i );
    else
      bands.push( [ i ] );
  }

  return bands;
}

function validateLayerCount( config: LevelConfig ): void
{
  if ( config.layers && config.layers.length > MAX_LEVEL_LAYERS )
    console.warn( `[LevelConfig] Level ${config.level}: ${config.layers.length} layers exceeds the supported maximum of ${MAX_LEVEL_LAYERS}` );
}

function validateTables( config: LevelConfig ): void
{
  if ( !config.tables )
    return;

  if ( config.tables.length > MAX_TABLES )
    console.warn( `[LevelConfig] Level ${config.level}: ${config.tables.length} tables exceeds the supported maximum of ${MAX_TABLES}` );

  const cols_limit = maxTableGridCols();
  const table_columns_by_index: number[] = [];

  config.tables.forEach( ( table, index ) =>
  {
    if ( table.layers.length > MAX_TABLE_LAYERS )
      console.warn( `[LevelConfig] Level ${config.level}, table ${index}: ${table.layers.length} layers exceeds the maximum of ${MAX_TABLE_LAYERS}` );

    let table_columns = 0;

    for ( const layer of table.layers )
    {
      if ( layer.grid.length > MAX_TABLE_GRID_ROWS )
        console.warn( `[LevelConfig] Level ${config.level}, table ${index}: grid has ${layer.grid.length} rows, maximum is ${MAX_TABLE_GRID_ROWS}` );

      const cols = layer.grid[0]?.length ?? 0;
      if ( cols > cols_limit )
        console.warn( `[LevelConfig] Level ${config.level}, table ${index}: grid has ${cols} columns, maximum is ${cols_limit} per table` );

      table_columns = Math.max( table_columns, cols );
      validateLevelLayer( layer );
    }

    table_columns_by_index[index] = table_columns;
  } );

  depthBands( config.tables ).forEach( ( band, band_index ) =>
  {
    const spent_columns = band.reduce( ( sum, i ) => sum + ( table_columns_by_index[i] ?? 0 ), 0 );
    const budget = tableColumnBudget( band.length );

    if ( spent_columns > budget )
      console.warn( `[LevelConfig] Level ${config.level}: depth band ${band_index} (tables ${band.join( ", " )}) spends ${spent_columns} columns, budget is ${budget} for ${band.length} table(s) in one band` );
  } );
}

const ROTATABLE_COLUMN_TYPES = [
  LevelPieceType.Jar,
  LevelPieceType.Metal,
  LevelPieceType.RoundMetal,
  LevelPieceType.IronCan,
  LevelPieceType.Ice,
  LevelPieceType.Tree,
  LevelPieceType.Plastic,
  LevelPieceType.Column,
  LevelPieceType.RoundColumn,
];

function validateLevelLayer( layer: LevelLayer ): void
{
  for ( const piece of layer.pieces )
  {
    if ( ( piece.type === LevelPieceType.Cone || piece.type === LevelPieceType.Watermelon ) && ( piece.width !== 1 || piece.height !== 1 ) )
      console.warn( `[LevelConfig] Piece id=${piece.id}: Cone/Watermelon pieces must be 1x1, got ${piece.width}x${piece.height}` );

    if ( ROTATABLE_COLUMN_TYPES.includes( piece.type ) )
    {
      const is_plastic_2x2 = piece.type === LevelPieceType.Plastic
        && !piece.lying
        && piece.width === 2
        && piece.height === 2;
      const max_blocks = piece.type === LevelPieceType.Tree || piece.type === LevelPieceType.Plastic ? 5
        : piece.type === LevelPieceType.Column || piece.type === LevelPieceType.RoundColumn ? 4
        : 3;

      if ( piece.lying )
      {
        if ( piece.height !== 1 )
          console.warn( `[LevelConfig] Piece id=${piece.id}: lying piece must have height=1 (footprint written already rotated), got ${piece.width}x${piece.height}` );

        if ( !Number.isInteger( piece.width ) )
          console.warn( `[LevelConfig] Piece id=${piece.id}: size must be a whole number, got ${piece.width}` );
        else if ( piece.width < 1 || piece.width > max_blocks )
          console.warn( `[LevelConfig] Piece id=${piece.id}: size must be in range 1-${max_blocks}, got ${piece.width}` );
      }
      else if ( !is_plastic_2x2 )
      {
        if ( piece.width !== 1 && piece.height !== 1 )
          console.warn( `[LevelConfig] Piece id=${piece.id}: must have width 1 (vertical) or height 1 (rotated onto its side), got ${piece.width}x${piece.height}` );

        const blocks = Math.max( piece.width, piece.height );
        if ( !Number.isInteger( blocks ) )
          console.warn( `[LevelConfig] Piece id=${piece.id}: size must be a whole number, got ${blocks}` );
        else if ( blocks < 1 || blocks > max_blocks )
          console.warn( `[LevelConfig] Piece id=${piece.id}: size must be in range 1-${max_blocks}, got ${blocks}` );
      }
    }

    if ( ( piece.type === LevelPieceType.Cube || piece.type === LevelPieceType.Stone )
      && ( Math.round( piece.width * 2 ) !== piece.width * 2 || Math.round( piece.height * 2 ) !== piece.height * 2 ) )
      console.warn( `[LevelConfig] Piece id=${piece.id}: width/height must be multiples of 0.5, got ${piece.width}x${piece.height}` );

    if ( piece.variant !== undefined )
    {
      const max_variant = piece.type === LevelPieceType.Cube ? 2
        : piece.type === LevelPieceType.Jar ? 3
        : piece.type === LevelPieceType.Cone ? 10
        : piece.type === LevelPieceType.Metal ? 6
        : piece.type === LevelPieceType.RoundMetal ? 6
        : piece.type === LevelPieceType.IronCan ? 1
        : piece.type === LevelPieceType.Plastic ? 7

        : -1;

      if ( max_variant === -1 )
        console.warn( `[LevelConfig] Piece id=${piece.id}: this piece type has no selectable variant, got ${piece.variant}` );
      else if ( piece.variant < 0 || piece.variant > max_variant )
        console.warn( `[LevelConfig] Piece id=${piece.id}: variant ${piece.variant} out of range 0-${max_variant}` );
    }

    if ( piece.offsetX !== undefined && piece.offsetX !== 0 && piece.offsetX !== 0.5 )
      console.warn( `[LevelConfig] Piece id=${piece.id}: offsetX must be 0 or 0.5, got ${piece.offsetX}` );

    if ( piece.offsetY !== undefined && piece.offsetY !== 0 && piece.offsetY !== 0.5 )
      console.warn( `[LevelConfig] Piece id=${piece.id}: offsetY must be 0 or 0.5, got ${piece.offsetY}` );

    if ( piece.offsetZ !== undefined && piece.offsetZ !== 0 && piece.offsetZ !== 0.5 )
      console.warn( `[LevelConfig] Piece id=${piece.id}: offsetZ must be 0 or 0.5, got ${piece.offsetZ}` );

    for ( let row = piece.y; row < piece.y + piece.height; row++ )
    {
      for ( let col = piece.x; col < piece.x + piece.width; col++ )
      {
        const cell = layer.grid[row]?.[col];
        if ( cell !== piece.id )
          console.warn( `[LevelConfig] Piece id=${piece.id}: grid[${row}][${col}] is ${cell}, expected ${piece.id}` );
      }
    }
  }
}

const LEGACY_LEVELS_CONFIG: LevelConfig[] = [
  {
    level: 1,
    difficulty: PlayDialogDifficulty.Common,
    layers: [
      {
        grid: [
          [0, 0, 1, 0, 0, 0],
          [0, 0, 2, 3, 0, 0],
          [0, 4, 5, 6, 0, 0],
          [0, 7, 8, 9, 10, 0],
          [11, 12, 13, 14, 15, 0],
          [16, 17, 18, 19, 20, 21]
        ],
        pieces: [
          { id: 1, type: LevelPieceType.Cube, x: 2, y: 0, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 2, type: LevelPieceType.Cube, x: 2, y: 1, width: 1, height: 1, variant: 0 },
          { id: 3, type: LevelPieceType.Cube, x: 3, y: 1, width: 1, height: 1, variant: 0 },
          { id: 4, type: LevelPieceType.Cube, x: 1, y: 2, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 5, type: LevelPieceType.Cube, x: 2, y: 2, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 6, type: LevelPieceType.Cube, x: 3, y: 2, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 7, type: LevelPieceType.Cube, x: 1, y: 3, width: 1, height: 1, variant: 0 },
          { id: 8, type: LevelPieceType.Cube, x: 2, y: 3, width: 1, height: 1, variant: 0 },
          { id: 9, type: LevelPieceType.Cube, x: 3, y: 3, width: 1, height: 1, variant: 0 },
          { id: 10, type: LevelPieceType.Cube, x: 4, y: 3, width: 1, height: 1, variant: 0 },
          { id: 11, type: LevelPieceType.Cube, x: 0, y: 4, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 12, type: LevelPieceType.Cube, x: 1, y: 4, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 13, type: LevelPieceType.Cube, x: 2, y: 4, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 14, type: LevelPieceType.Cube, x: 3, y: 4, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 15, type: LevelPieceType.Cube, x: 4, y: 4, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 16, type: LevelPieceType.Cube, x: 0, y: 5, width: 1, height: 1, variant: 0 },
          { id: 17, type: LevelPieceType.Cube, x: 1, y: 5, width: 1, height: 1, variant: 0 },
          { id: 18, type: LevelPieceType.Cube, x: 2, y: 5, width: 1, height: 1, variant: 0 },
          { id: 19, type: LevelPieceType.Cube, x: 3, y: 5, width: 1, height: 1, variant: 0 },
          { id: 20, type: LevelPieceType.Cube, x: 4, y: 5, width: 1, height: 1, variant: 0 },
          { id: 21, type: LevelPieceType.Cube, x: 5, y: 5, width: 1, height: 1, variant: 0 },
        ],
      },
    ],
  },
  {
    level: 2,
    difficulty: PlayDialogDifficulty.Common,
    layers: [
      {
        grid: [
          [1, 1, 1, 2, 2, 2],
          [3, 0, 4, 0, 0, 5],
          [6, 0, 7, 0, 0, 8],
          [9, 9, 9, 10, 10, 10],
          [11, 0, 12, 0, 0, 13],
          [14, 0, 15, 0, 0, 16]
        ],
        pieces: [
          { id: 1, type: LevelPieceType.Cube, x: 0, y: 0, width: 3, height: 1, variant: 0 },
          { id: 2, type: LevelPieceType.Cube, x: 3, y: 0, width: 3, height: 1, variant: 0 },
          { id: 3, type: LevelPieceType.Cube, x: 0, y: 1, width: 1, height: 1, variant: 0 },
          { id: 4, type: LevelPieceType.Cube, x: 2, y: 1, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 5, type: LevelPieceType.Cube, x: 5, y: 1, width: 1, height: 1, variant: 0 },
          { id: 6, type: LevelPieceType.Cube, x: 0, y: 2, width: 1, height: 1, variant: 0 },
          { id: 7, type: LevelPieceType.Cube, x: 2, y: 2, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 8, type: LevelPieceType.Cube, x: 5, y: 2, width: 1, height: 1, variant: 0 },
          { id: 9, type: LevelPieceType.Cube, x: 0, y: 3, width: 3, height: 1, variant: 0 },
          { id: 10, type: LevelPieceType.Cube, x: 3, y: 3, width: 3, height: 1, variant: 0 },
          { id: 11, type: LevelPieceType.Cube, x: 0, y: 4, width: 1, height: 1, variant: 0 },
          { id: 12, type: LevelPieceType.Cube, x: 2, y: 4, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 13, type: LevelPieceType.Cube, x: 5, y: 4, width: 1, height: 1, variant: 0 },
          { id: 14, type: LevelPieceType.Cube, x: 0, y: 5, width: 1, height: 1, variant: 0 },
          { id: 15, type: LevelPieceType.Cube, x: 2, y: 5, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 16, type: LevelPieceType.Cube, x: 5, y: 5, width: 1, height: 1, variant: 0 },
        ],
      },
    ],
  },
  {
    level: 3,
    difficulty: PlayDialogDifficulty.Common,
    layers: [
      {
        grid: [
          [1, 2, 3, 4, 5, 6],
          [7, 8, 9, 10, 11, 12],
          [13, 13, 13, 14, 14, 14],
          [15, 16, 17, 18, 19, 0],
          [20, 21, 22, 23, 24, 0],
          [25, 26, 27, 28, 29, 0]
        ],
        pieces: [
          { id: 1, type: LevelPieceType.Cone, x: 0, y: 0, width: 1, height: 1, variant: 1 },
          { id: 2, type: LevelPieceType.Cone, x: 1, y: 0, width: 1, height: 1, variant: 1 },
          { id: 3, type: LevelPieceType.Cone, x: 2, y: 0, width: 1, height: 1, variant: 1 },
          { id: 4, type: LevelPieceType.Cone, x: 3, y: 0, width: 1, height: 1, variant: 1 },
          { id: 5, type: LevelPieceType.Cone, x: 4, y: 0, width: 1, height: 1, variant: 1 },
          { id: 6, type: LevelPieceType.Cone, x: 5, y: 0, width: 1, height: 1, variant: 1 },
          { id: 7, type: LevelPieceType.Cube, x: 0, y: 1, width: 1, height: 1, variant: 0 },
          { id: 8, type: LevelPieceType.Cube, x: 1, y: 1, width: 1, height: 1, variant: 0 },
          { id: 9, type: LevelPieceType.Cube, x: 2, y: 1, width: 1, height: 1, variant: 0 },
          { id: 10, type: LevelPieceType.Cube, x: 3, y: 1, width: 1, height: 1, variant: 0 },
          { id: 11, type: LevelPieceType.Cube, x: 4, y: 1, width: 1, height: 1, variant: 0 },
          { id: 12, type: LevelPieceType.Cube, x: 5, y: 1, width: 1, height: 1, variant: 0 },
          { id: 13, type: LevelPieceType.Cube, x: 0, y: 2, width: 3, height: 1, variant: 0 },
          { id: 14, type: LevelPieceType.Cube, x: 3, y: 2, width: 3, height: 1, variant: 0 },
          { id: 15, type: LevelPieceType.Cube, x: 0, y: 3, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 16, type: LevelPieceType.Cube, x: 1, y: 3, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 17, type: LevelPieceType.Cube, x: 2, y: 3, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 18, type: LevelPieceType.Cube, x: 3, y: 3, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 19, type: LevelPieceType.Cube, x: 4, y: 3, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 20, type: LevelPieceType.Cube, x: 0, y: 4, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 21, type: LevelPieceType.Cube, x: 1, y: 4, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 22, type: LevelPieceType.Cube, x: 2, y: 4, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 23, type: LevelPieceType.Cube, x: 3, y: 4, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 24, type: LevelPieceType.Cube, x: 4, y: 4, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 25, type: LevelPieceType.Cube, x: 0, y: 5, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 26, type: LevelPieceType.Cube, x: 1, y: 5, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 27, type: LevelPieceType.Cube, x: 2, y: 5, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 28, type: LevelPieceType.Cube, x: 3, y: 5, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 29, type: LevelPieceType.Cube, x: 4, y: 5, width: 1, height: 1, variant: 0, offsetX: 0.5 },
        ],
      },
    ],
  },
    {
    level: 4,
    difficulty: PlayDialogDifficulty.Common,
    layers: [
      {
        grid: [
          [1, 2, 3, 4, 5, 6],
          [7, 8, 9, 10, 11, 12],
          [13, 14, 15, 16, 17, 18],
          [19, 20, 21, 22, 23, 24],
          [25, 26, 27, 28, 29, 30]
        ],
        pieces: [
          { id: 1, type: LevelPieceType.Cone, x: 0, y: 0, width: 1, height: 1, variant: 1 },
          { id: 2, type: LevelPieceType.Cone, x: 1, y: 0, width: 1, height: 1, variant: 1 },
          { id: 3, type: LevelPieceType.Cone, x: 2, y: 0, width: 1, height: 1, variant: 1 },
          { id: 4, type: LevelPieceType.Cone, x: 3, y: 0, width: 1, height: 1, variant: 1 },
          { id: 5, type: LevelPieceType.Cone, x: 4, y: 0, width: 1, height: 1, variant: 1 },
          { id: 6, type: LevelPieceType.Cone, x: 5, y: 0, width: 1, height: 1, variant: 1 },
          { id: 7, type: LevelPieceType.Cube, x: 0, y: 1, width: 1, height: 1, variant: 0 },
          { id: 8, type: LevelPieceType.Cube, x: 1, y: 1, width: 1, height: 1, variant: 0 },
          { id: 9, type: LevelPieceType.Cube, x: 2, y: 1, width: 1, height: 1, variant: 0 },
          { id: 10, type: LevelPieceType.Cube, x: 3, y: 1, width: 1, height: 1, variant: 0 },
          { id: 11, type: LevelPieceType.Cube, x: 4, y: 1, width: 1, height: 1, variant: 0 },
          { id: 12, type: LevelPieceType.Cube, x: 5, y: 1, width: 1, height: 1, variant: 0 },
          { id: 13, type: LevelPieceType.Cube, x: 0, y: 2, width: 1, height: 1, variant: 0 },
          { id: 14, type: LevelPieceType.Cube, x: 1, y: 2, width: 1, height: 1, variant: 0 },
          { id: 15, type: LevelPieceType.Cube, x: 2, y: 2, width: 1, height: 1, variant: 0 },
          { id: 16, type: LevelPieceType.Cube, x: 3, y: 2, width: 1, height: 1, variant: 0 },
          { id: 17, type: LevelPieceType.Cube, x: 4, y: 2, width: 1, height: 1, variant: 0 },
          { id: 18, type: LevelPieceType.Cube, x: 5, y: 2, width: 1, height: 1, variant: 0 },
          { id: 19, type: LevelPieceType.Cube, x: 0, y: 3, width: 1, height: 1, variant: 1 },
          { id: 20, type: LevelPieceType.Cube, x: 1, y: 3, width: 1, height: 1, variant: 1 },
          { id: 21, type: LevelPieceType.Cube, x: 2, y: 3, width: 1, height: 1, variant: 1 },
          { id: 22, type: LevelPieceType.Cube, x: 3, y: 3, width: 1, height: 1, variant: 1 },
          { id: 23, type: LevelPieceType.Cube, x: 4, y: 3, width: 1, height: 1, variant: 1 },
          { id: 24, type: LevelPieceType.Cube, x: 5, y: 3, width: 1, height: 1, variant: 1 },
          { id: 25, type: LevelPieceType.Cube, x: 0, y: 4, width: 1, height: 1, variant: 1 },
          { id: 26, type: LevelPieceType.Cube, x: 1, y: 4, width: 1, height: 1, variant: 1 },
          { id: 27, type: LevelPieceType.Cube, x: 2, y: 4, width: 1, height: 1, variant: 1 },
          { id: 28, type: LevelPieceType.Cube, x: 3, y: 4, width: 1, height: 1, variant: 1 },
          { id: 29, type: LevelPieceType.Cube, x: 4, y: 4, width: 1, height: 1, variant: 1 },
          { id: 30, type: LevelPieceType.Cube, x: 5, y: 4, width: 1, height: 1, variant: 1 },
        ],
      },
    ],
  },
  {
    level: 5,
    difficulty: PlayDialogDifficulty.Common,
    layers: [
      {
        grid: [
          [1, 2, 3, 4, 5, 6],
          [7, 8, 9, 10, 11, 12],
          [13, 14, 15, 16, 17, 18],
          [19, 20, 21, 22, 23, 24],
          [25, 26, 27, 28, 29, 30],
          [31, 32, 33, 34, 35, 36]
        ],
        pieces: [
          { id: 1, type: LevelPieceType.Cube, x: 0, y: 0, width: 1, height: 1, variant: 1 },
          { id: 2, type: LevelPieceType.Cube, x: 1, y: 0, width: 1, height: 1, variant: 1 },
          { id: 3, type: LevelPieceType.Cube, x: 2, y: 0, width: 1, height: 1, variant: 0 },
          { id: 4, type: LevelPieceType.Cube, x: 3, y: 0, width: 1, height: 1, variant: 0 },
          { id: 5, type: LevelPieceType.Cube, x: 4, y: 0, width: 1, height: 1, variant: 1 },
          { id: 6, type: LevelPieceType.Cube, x: 5, y: 0, width: 1, height: 1, variant: 1 },
          { id: 7, type: LevelPieceType.Cube, x: 0, y: 1, width: 1, height: 1, variant: 1 },
          { id: 8, type: LevelPieceType.Cube, x: 1, y: 1, width: 1, height: 1, variant: 1 },
          { id: 9, type: LevelPieceType.Cube, x: 2, y: 1, width: 1, height: 1, variant: 0 },
          { id: 10, type: LevelPieceType.Cube, x: 3, y: 1, width: 1, height: 1, variant: 0 },
          { id: 11, type: LevelPieceType.Cube, x: 4, y: 1, width: 1, height: 1, variant: 1 },
          { id: 12, type: LevelPieceType.Cube, x: 5, y: 1, width: 1, height: 1, variant: 1 },
          { id: 13, type: LevelPieceType.Cube, x: 0, y: 2, width: 1, height: 1, variant: 0 },
          { id: 14, type: LevelPieceType.Cube, x: 1, y: 2, width: 1, height: 1, variant: 0 },
          { id: 15, type: LevelPieceType.Cube, x: 2, y: 2, width: 1, height: 1, variant: 0 },
          { id: 16, type: LevelPieceType.Cube, x: 3, y: 2, width: 1, height: 1, variant: 0 },
          { id: 17, type: LevelPieceType.Cube, x: 4, y: 2, width: 1, height: 1, variant: 0 },
          { id: 18, type: LevelPieceType.Cube, x: 5, y: 2, width: 1, height: 1, variant: 0 },
          { id: 19, type: LevelPieceType.Cube, x: 0, y: 3, width: 1, height: 1, variant: 0 },
          { id: 20, type: LevelPieceType.Cube, x: 1, y: 3, width: 1, height: 1, variant: 0 },
          { id: 21, type: LevelPieceType.Cube, x: 2, y: 3, width: 1, height: 1, variant: 0 },
          { id: 22, type: LevelPieceType.Cube, x: 3, y: 3, width: 1, height: 1, variant: 0 },
          { id: 23, type: LevelPieceType.Cube, x: 4, y: 3, width: 1, height: 1, variant: 0 },
          { id: 24, type: LevelPieceType.Cube, x: 5, y: 3, width: 1, height: 1, variant: 0 },
          { id: 25, type: LevelPieceType.Cube, x: 0, y: 4, width: 1, height: 1, variant: 1 },
          { id: 26, type: LevelPieceType.Cube, x: 1, y: 4, width: 1, height: 1, variant: 1 },
          { id: 27, type: LevelPieceType.Cube, x: 2, y: 4, width: 1, height: 1, variant: 0 },
          { id: 28, type: LevelPieceType.Cube, x: 3, y: 4, width: 1, height: 1, variant: 0 },
          { id: 29, type: LevelPieceType.Cube, x: 4, y: 4, width: 1, height: 1, variant: 1 },
          { id: 30, type: LevelPieceType.Cube, x: 5, y: 4, width: 1, height: 1, variant: 1 },
          { id: 31, type: LevelPieceType.Cube, x: 0, y: 5, width: 1, height: 1, variant: 1 },
          { id: 32, type: LevelPieceType.Cube, x: 1, y: 5, width: 1, height: 1, variant: 1 },
          { id: 33, type: LevelPieceType.Cube, x: 2, y: 5, width: 1, height: 1, variant: 0 },
          { id: 34, type: LevelPieceType.Cube, x: 3, y: 5, width: 1, height: 1, variant: 0 },
          { id: 35, type: LevelPieceType.Cube, x: 4, y: 5, width: 1, height: 1, variant: 1 },
          { id: 36, type: LevelPieceType.Cube, x: 5, y: 5, width: 1, height: 1, variant: 1 },
        ],
      },
    ],
  },
  {
    level: 6,
    difficulty: PlayDialogDifficulty.Common,
    layers: [
      {
        grid: [
          [1, 2, 0, 0, 3, 4],
          [5, 6, 0, 0, 7, 8],
          [9, 10, 0, 0, 11, 12],
          [13, 14, 0, 0, 15, 16],
          [17, 18, 0, 0, 19, 20],
          [21, 22, 0, 0, 23, 24]
        ],
        pieces: [
          { id: 1, type: LevelPieceType.Jar, x: 0, y: 0, width: 1, height: 1, variant: 2 },
          { id: 2, type: LevelPieceType.Jar, x: 1, y: 0, width: 1, height: 1, variant: 2 },
          { id: 3, type: LevelPieceType.Jar, x: 4, y: 0, width: 1, height: 1, variant: 2 },
          { id: 4, type: LevelPieceType.Jar, x: 5, y: 0, width: 1, height: 1, variant: 2 },
          { id: 5, type: LevelPieceType.Jar, x: 0, y: 1, width: 1, height: 1, variant: 2 },
          { id: 6, type: LevelPieceType.Jar, x: 1, y: 1, width: 1, height: 1, variant: 2 },
          { id: 7, type: LevelPieceType.Jar, x: 4, y: 1, width: 1, height: 1, variant: 2 },
          { id: 8, type: LevelPieceType.Jar, x: 5, y: 1, width: 1, height: 1, variant: 2 },
          { id: 9, type: LevelPieceType.Jar, x: 0, y: 2, width: 1, height: 1, variant: 2 },
          { id: 10, type: LevelPieceType.Jar, x: 1, y: 2, width: 1, height: 1, variant: 2 },
          { id: 11, type: LevelPieceType.Jar, x: 4, y: 2, width: 1, height: 1, variant: 2 },
          { id: 12, type: LevelPieceType.Jar, x: 5, y: 2, width: 1, height: 1, variant: 2 },
          { id: 13, type: LevelPieceType.Jar, x: 0, y: 3, width: 1, height: 1, variant: 2 },
          { id: 14, type: LevelPieceType.Jar, x: 1, y: 3, width: 1, height: 1, variant: 2 },
          { id: 15, type: LevelPieceType.Jar, x: 4, y: 3, width: 1, height: 1, variant: 2 },
          { id: 16, type: LevelPieceType.Jar, x: 5, y: 3, width: 1, height: 1, variant: 2 },
          { id: 17, type: LevelPieceType.Jar, x: 0, y: 4, width: 1, height: 1, variant: 2 },
          { id: 18, type: LevelPieceType.Jar, x: 1, y: 4, width: 1, height: 1, variant: 2 },
          { id: 19, type: LevelPieceType.Jar, x: 4, y: 4, width: 1, height: 1, variant: 2 },
          { id: 20, type: LevelPieceType.Jar, x: 5, y: 4, width: 1, height: 1, variant: 2 },
          { id: 21, type: LevelPieceType.Jar, x: 0, y: 5, width: 1, height: 1, variant: 2 },
          { id: 22, type: LevelPieceType.Jar, x: 1, y: 5, width: 1, height: 1, variant: 2 },
          { id: 23, type: LevelPieceType.Jar, x: 4, y: 5, width: 1, height: 1, variant: 2 },
          { id: 24, type: LevelPieceType.Jar, x: 5, y: 5, width: 1, height: 1, variant: 2 },
        ],
      },
    ],
  },
  {
    level: 7,
    difficulty: PlayDialogDifficulty.Common,
    layers: [
      {
        grid: [
          [1, 2, 3, 4, 5, 6],
          [7, 8, 9, 10, 11, 12],
          [13, 14, 15, 16, 17, 18],
          [19, 20, 21, 22, 23, 24],
          [25, 26, 27, 28, 29, 30],
          [31, 32, 33, 34, 35, 36]
        ],
        pieces: [
          { id: 1, type: LevelPieceType.Cube, x: 0, y: 0, width: 1, height: 1, variant: 1 },
          { id: 2, type: LevelPieceType.Cube, x: 1, y: 0, width: 1, height: 1, variant: 1 },
          { id: 3, type: LevelPieceType.Cube, x: 2, y: 0, width: 1, height: 1, variant: 1 },
          { id: 4, type: LevelPieceType.Cube, x: 3, y: 0, width: 1, height: 1, variant: 1 },
          { id: 5, type: LevelPieceType.Cube, x: 4, y: 0, width: 1, height: 1, variant: 1 },
          { id: 6, type: LevelPieceType.Cube, x: 5, y: 0, width: 1, height: 1, variant: 1 },
          { id: 7, type: LevelPieceType.Cube, x: 0, y: 1, width: 1, height: 1, variant: 1 },
          { id: 8, type: LevelPieceType.Cube, x: 1, y: 1, width: 1, height: 1, variant: 1 },
          { id: 9, type: LevelPieceType.Cube, x: 2, y: 1, width: 1, height: 1, variant: 1 },
          { id: 10, type: LevelPieceType.Cube, x: 3, y: 1, width: 1, height: 1, variant: 1 },
          { id: 11, type: LevelPieceType.Cube, x: 4, y: 1, width: 1, height: 1, variant: 1 },
          { id: 12, type: LevelPieceType.Cube, x: 5, y: 1, width: 1, height: 1, variant: 1 },
          { id: 13, type: LevelPieceType.Cube, x: 0, y: 2, width: 1, height: 1, variant: 1 },
          { id: 14, type: LevelPieceType.Cube, x: 1, y: 2, width: 1, height: 1, variant: 1 },
          { id: 15, type: LevelPieceType.Cube, x: 2, y: 2, width: 1, height: 1, variant: 1 },
          { id: 16, type: LevelPieceType.Cube, x: 3, y: 2, width: 1, height: 1, variant: 1 },
          { id: 17, type: LevelPieceType.Cube, x: 4, y: 2, width: 1, height: 1, variant: 1 },
          { id: 18, type: LevelPieceType.Cube, x: 5, y: 2, width: 1, height: 1, variant: 1 },
          { id: 19, type: LevelPieceType.Cube, x: 0, y: 3, width: 1, height: 1, variant: 1 },
          { id: 20, type: LevelPieceType.Cube, x: 1, y: 3, width: 1, height: 1, variant: 1 },
          { id: 21, type: LevelPieceType.Cube, x: 2, y: 3, width: 1, height: 1, variant: 1 },
          { id: 22, type: LevelPieceType.Cube, x: 3, y: 3, width: 1, height: 1, variant: 1 },
          { id: 23, type: LevelPieceType.Cube, x: 4, y: 3, width: 1, height: 1, variant: 1 },
          { id: 24, type: LevelPieceType.Cube, x: 5, y: 3, width: 1, height: 1, variant: 1 },
          { id: 25, type: LevelPieceType.Cube, x: 0, y: 4, width: 1, height: 1, variant: 1 },
          { id: 26, type: LevelPieceType.Cube, x: 1, y: 4, width: 1, height: 1, variant: 1 },
          { id: 27, type: LevelPieceType.Cube, x: 2, y: 4, width: 1, height: 1, variant: 1 },
          { id: 28, type: LevelPieceType.Cube, x: 3, y: 4, width: 1, height: 1, variant: 1 },
          { id: 29, type: LevelPieceType.Cube, x: 4, y: 4, width: 1, height: 1, variant: 1 },
          { id: 30, type: LevelPieceType.Cube, x: 5, y: 4, width: 1, height: 1, variant: 1 },
          { id: 31, type: LevelPieceType.Cube, x: 0, y: 5, width: 1, height: 1, variant: 1 },
          { id: 32, type: LevelPieceType.Cube, x: 1, y: 5, width: 1, height: 1, variant: 1 },
          { id: 33, type: LevelPieceType.Cube, x: 2, y: 5, width: 1, height: 1, variant: 1 },
          { id: 34, type: LevelPieceType.Cube, x: 3, y: 5, width: 1, height: 1, variant: 1 },
          { id: 35, type: LevelPieceType.Cube, x: 4, y: 5, width: 1, height: 1, variant: 1 },
          { id: 36, type: LevelPieceType.Cube, x: 5, y: 5, width: 1, height: 1, variant: 1 },
        ],
      },
    ],
  },
  {
    level: 8,
    difficulty: PlayDialogDifficulty.Common,
    layers: [
      {
        grid: [
          [0, 0, 0, 0, 0, 0, 0, 0, 0],
          [1, 0, 2, 0, 3, 0, 4, 0, 5],
          [6, 0, 7, 0, 8, 0, 9, 0, 10],
          [11, 0, 12, 0, 13, 0, 14, 0, 15],
          [16, 0, 17, 0, 18, 0, 19, 0, 20],
          [21, 21, 21, 22, 22, 22, 23, 23, 23],
          [0, 24, 0, 25, 0, 26, 0, 27, 0],
          [0, 28, 0, 29, 0, 30, 0, 31, 0],
          [0, 32, 0, 33, 0, 34, 0, 35, 0]
        ],
        pieces: [
          { id: 1, type: LevelPieceType.Cone, x: 0, y: 1, width: 1, height: 1, variant: 1 },
          { id: 2, type: LevelPieceType.Cone, x: 2, y: 1, width: 1, height: 1, variant: 1 },
          { id: 3, type: LevelPieceType.Cone, x: 4, y: 1, width: 1, height: 1, variant: 1 },
          { id: 4, type: LevelPieceType.Cone, x: 6, y: 1, width: 1, height: 1, variant: 1 },
          { id: 5, type: LevelPieceType.Cone, x: 8, y: 1, width: 1, height: 1, variant: 1 },
          { id: 6, type: LevelPieceType.Cube, x: 0, y: 2, width: 1, height: 1, variant: 0 },
          { id: 7, type: LevelPieceType.Cube, x: 2, y: 2, width: 1, height: 1, variant: 0 },
          { id: 8, type: LevelPieceType.Cube, x: 4, y: 2, width: 1, height: 1, variant: 0 },
          { id: 9, type: LevelPieceType.Cube, x: 6, y: 2, width: 1, height: 1, variant: 0 },
          { id: 10, type: LevelPieceType.Cube, x: 8, y: 2, width: 1, height: 1, variant: 0 },
          { id: 11, type: LevelPieceType.Cube, x: 0, y: 3, width: 1, height: 1, variant: 0 },
          { id: 12, type: LevelPieceType.Cube, x: 2, y: 3, width: 1, height: 1, variant: 0 },
          { id: 13, type: LevelPieceType.Cube, x: 4, y: 3, width: 1, height: 1, variant: 0 },
          { id: 14, type: LevelPieceType.Cube, x: 6, y: 3, width: 1, height: 1, variant: 0 },
          { id: 15, type: LevelPieceType.Cube, x: 8, y: 3, width: 1, height: 1, variant: 0 },
          { id: 16, type: LevelPieceType.Cube, x: 0, y: 4, width: 1, height: 1, variant: 0 },
          { id: 17, type: LevelPieceType.Cube, x: 2, y: 4, width: 1, height: 1, variant: 0 },
          { id: 18, type: LevelPieceType.Cube, x: 4, y: 4, width: 1, height: 1, variant: 0 },
          { id: 19, type: LevelPieceType.Cube, x: 6, y: 4, width: 1, height: 1, variant: 0 },
          { id: 20, type: LevelPieceType.Cube, x: 8, y: 4, width: 1, height: 1, variant: 0 },
          { id: 21, type: LevelPieceType.Cube, x: 0, y: 5, width: 3, height: 1, variant: 0 },
          { id: 22, type: LevelPieceType.Cube, x: 3, y: 5, width: 3, height: 1, variant: 0 },
          { id: 23, type: LevelPieceType.Cube, x: 6, y: 5, width: 3, height: 1, variant: 0 },
          { id: 24, type: LevelPieceType.Cube, x: 1, y: 6, width: 1, height: 1, variant: 0 },
          { id: 25, type: LevelPieceType.Cube, x: 3, y: 6, width: 1, height: 1, variant: 0 },
          { id: 26, type: LevelPieceType.Cube, x: 5, y: 6, width: 1, height: 1, variant: 0 },
          { id: 27, type: LevelPieceType.Cube, x: 7, y: 6, width: 1, height: 1, variant: 0 },
          { id: 28, type: LevelPieceType.Cube, x: 1, y: 7, width: 1, height: 1, variant: 0 },
          { id: 29, type: LevelPieceType.Cube, x: 3, y: 7, width: 1, height: 1, variant: 0 },
          { id: 30, type: LevelPieceType.Cube, x: 5, y: 7, width: 1, height: 1, variant: 0 },
          { id: 31, type: LevelPieceType.Cube, x: 7, y: 7, width: 1, height: 1, variant: 0 },
          { id: 32, type: LevelPieceType.Cube, x: 1, y: 8, width: 1, height: 1, variant: 0 },
          { id: 33, type: LevelPieceType.Cube, x: 3, y: 8, width: 1, height: 1, variant: 0 },
          { id: 34, type: LevelPieceType.Cube, x: 5, y: 8, width: 1, height: 1, variant: 0 },
          { id: 35, type: LevelPieceType.Cube, x: 7, y: 8, width: 1, height: 1, variant: 0 },
        ],
      },
    ],
  },
  {
    level: 9,
    difficulty: PlayDialogDifficulty.Common,
    layers: [
      {
        grid: [
          [1, 2, 3, 4, 5, 6, 7],
          [8, 9, 10, 11, 12, 13, 14],
          [15, 16, 17, 18, 19, 20, 21],
          [22, 23, 24, 25, 26, 27, 28],
          [29, 30, 31, 32, 33, 34, 35],
          [36, 37, 38, 39, 40, 41, 42],
          [43, 44, 45, 46, 47, 48, 49]
        ],
        pieces: [
          { id: 1, type: LevelPieceType.Jar, x: 0, y: 0, width: 1, height: 1, variant: 2 },
          { id: 2, type: LevelPieceType.Jar, x: 1, y: 0, width: 1, height: 1, variant: 2 },
          { id: 3, type: LevelPieceType.Jar, x: 2, y: 0, width: 1, height: 1, variant: 2 },
          { id: 4, type: LevelPieceType.Jar, x: 3, y: 0, width: 1, height: 1, variant: 2 },
          { id: 5, type: LevelPieceType.Jar, x: 4, y: 0, width: 1, height: 1, variant: 2 },
          { id: 6, type: LevelPieceType.Jar, x: 5, y: 0, width: 1, height: 1, variant: 2 },
          { id: 7, type: LevelPieceType.Jar, x: 6, y: 0, width: 1, height: 1, variant: 2 },
          { id: 8, type: LevelPieceType.Jar, x: 0, y: 1, width: 1, height: 1, variant: 2 },
          { id: 9, type: LevelPieceType.Cube, x: 1, y: 1, width: 1, height: 1, variant: 1 },
          { id: 10, type: LevelPieceType.Cube, x: 2, y: 1, width: 1, height: 1, variant: 1 },
          { id: 11, type: LevelPieceType.Cube, x: 3, y: 1, width: 1, height: 1, variant: 1 },
          { id: 12, type: LevelPieceType.Cube, x: 4, y: 1, width: 1, height: 1, variant: 1 },
          { id: 13, type: LevelPieceType.Cube, x: 5, y: 1, width: 1, height: 1, variant: 1 },
          { id: 14, type: LevelPieceType.Jar, x: 6, y: 1, width: 1, height: 1, variant: 2 },
          { id: 15, type: LevelPieceType.Jar, x: 0, y: 2, width: 1, height: 1, variant: 2 },
          { id: 16, type: LevelPieceType.Cube, x: 1, y: 2, width: 1, height: 1, variant: 1 },
          { id: 17, type: LevelPieceType.Jar, x: 2, y: 2, width: 1, height: 1, variant: 2 },
          { id: 18, type: LevelPieceType.Jar, x: 3, y: 2, width: 1, height: 1, variant: 2 },
          { id: 19, type: LevelPieceType.Jar, x: 4, y: 2, width: 1, height: 1, variant: 2 },
          { id: 20, type: LevelPieceType.Cube, x: 5, y: 2, width: 1, height: 1, variant: 1 },
          { id: 21, type: LevelPieceType.Jar, x: 6, y: 2, width: 1, height: 1, variant: 2 },
          { id: 22, type: LevelPieceType.Jar, x: 0, y: 3, width: 1, height: 1, variant: 2 },
          { id: 23, type: LevelPieceType.Cube, x: 1, y: 3, width: 1, height: 1, variant: 1 },
          { id: 24, type: LevelPieceType.Jar, x: 2, y: 3, width: 1, height: 1, variant: 2 },
          { id: 25, type: LevelPieceType.Cube, x: 3, y: 3, width: 1, height: 1, variant: 1 },
          { id: 26, type: LevelPieceType.Jar, x: 4, y: 3, width: 1, height: 1, variant: 2 },
          { id: 27, type: LevelPieceType.Cube, x: 5, y: 3, width: 1, height: 1, variant: 1 },
          { id: 28, type: LevelPieceType.Jar, x: 6, y: 3, width: 1, height: 1, variant: 2 },
          { id: 29, type: LevelPieceType.Jar, x: 0, y: 4, width: 1, height: 1, variant: 2 },
          { id: 30, type: LevelPieceType.Cube, x: 1, y: 4, width: 1, height: 1, variant: 1 },
          { id: 31, type: LevelPieceType.Jar, x: 2, y: 4, width: 1, height: 1, variant: 2 },
          { id: 32, type: LevelPieceType.Jar, x: 3, y: 4, width: 1, height: 1, variant: 2 },
          { id: 33, type: LevelPieceType.Jar, x: 4, y: 4, width: 1, height: 1, variant: 2 },
          { id: 34, type: LevelPieceType.Cube, x: 5, y: 4, width: 1, height: 1, variant: 1 },
          { id: 35, type: LevelPieceType.Jar, x: 6, y: 4, width: 1, height: 1, variant: 2 },
          { id: 36, type: LevelPieceType.Jar, x: 0, y: 5, width: 1, height: 1, variant: 2 },
          { id: 37, type: LevelPieceType.Cube, x: 1, y: 5, width: 1, height: 1, variant: 1 },
          { id: 38, type: LevelPieceType.Cube, x: 2, y: 5, width: 1, height: 1, variant: 1 },
          { id: 39, type: LevelPieceType.Cube, x: 3, y: 5, width: 1, height: 1, variant: 1 },
          { id: 40, type: LevelPieceType.Cube, x: 4, y: 5, width: 1, height: 1, variant: 1 },
          { id: 41, type: LevelPieceType.Cube, x: 5, y: 5, width: 1, height: 1, variant: 1 },
          { id: 42, type: LevelPieceType.Jar, x: 6, y: 5, width: 1, height: 1, variant: 2 },
          { id: 43, type: LevelPieceType.Jar, x: 0, y: 6, width: 1, height: 1, variant: 2 },
          { id: 44, type: LevelPieceType.Jar, x: 1, y: 6, width: 1, height: 1, variant: 2 },
          { id: 45, type: LevelPieceType.Jar, x: 2, y: 6, width: 1, height: 1, variant: 2 },
          { id: 46, type: LevelPieceType.Jar, x: 3, y: 6, width: 1, height: 1, variant: 2 },
          { id: 47, type: LevelPieceType.Jar, x: 4, y: 6, width: 1, height: 1, variant: 2 },
          { id: 48, type: LevelPieceType.Jar, x: 5, y: 6, width: 1, height: 1, variant: 2 },
          { id: 49, type: LevelPieceType.Jar, x: 6, y: 6, width: 1, height: 1, variant: 2 },
        ],
      },
    ],
  },
  {
    level: 10,
    difficulty: PlayDialogDifficulty.Common,
    layers: [
      {
        grid: [
          [1, 2, 3, 4, 5, 6],
          [7, 8, 9, 10, 11, 12],
          [13, 14, 15, 16, 17, 18],
          [19, 20, 21, 22, 23, 24],
          [25, 26, 27, 28, 29, 30],
          [31, 32, 33, 34, 35, 36]
        ],
        pieces: [
          { id: 1, type: LevelPieceType.Jar, x: 0, y: 0, width: 1, height: 1, variant: 0 },
          { id: 2, type: LevelPieceType.Jar, x: 1, y: 0, width: 1, height: 1, variant: 0 },
          { id: 3, type: LevelPieceType.Jar, x: 2, y: 0, width: 1, height: 1, variant: 3 },
          { id: 4, type: LevelPieceType.Jar, x: 3, y: 0, width: 1, height: 1, variant: 3 },
          { id: 5, type: LevelPieceType.Jar, x: 4, y: 0, width: 1, height: 1, variant: 0 },
          { id: 6, type: LevelPieceType.Jar, x: 5, y: 0, width: 1, height: 1, variant: 0 },
          { id: 7, type: LevelPieceType.Jar, x: 0, y: 1, width: 1, height: 1, variant: 0 },
          { id: 8, type: LevelPieceType.Jar, x: 1, y: 1, width: 1, height: 1, variant: 0 },
          { id: 9, type: LevelPieceType.Jar, x: 2, y: 1, width: 1, height: 1, variant: 3 },
          { id: 10, type: LevelPieceType.Jar, x: 3, y: 1, width: 1, height: 1, variant: 3 },
          { id: 11, type: LevelPieceType.Jar, x: 4, y: 1, width: 1, height: 1, variant: 0 },
          { id: 12, type: LevelPieceType.Jar, x: 5, y: 1, width: 1, height: 1, variant: 0 },
          { id: 13, type: LevelPieceType.Jar, x: 0, y: 2, width: 1, height: 1, variant: 3 },
          { id: 14, type: LevelPieceType.Jar, x: 1, y: 2, width: 1, height: 1, variant: 3 },
          { id: 15, type: LevelPieceType.Jar, x: 2, y: 2, width: 1, height: 1, variant: 0 },
          { id: 16, type: LevelPieceType.Jar, x: 3, y: 2, width: 1, height: 1, variant: 0 },
          { id: 17, type: LevelPieceType.Jar, x: 4, y: 2, width: 1, height: 1, variant: 3 },
          { id: 18, type: LevelPieceType.Jar, x: 5, y: 2, width: 1, height: 1, variant: 3 },
          { id: 19, type: LevelPieceType.Jar, x: 0, y: 3, width: 1, height: 1, variant: 3 },
          { id: 20, type: LevelPieceType.Jar, x: 1, y: 3, width: 1, height: 1, variant: 3 },
          { id: 21, type: LevelPieceType.Jar, x: 2, y: 3, width: 1, height: 1, variant: 0 },
          { id: 22, type: LevelPieceType.Jar, x: 3, y: 3, width: 1, height: 1, variant: 0 },
          { id: 23, type: LevelPieceType.Jar, x: 4, y: 3, width: 1, height: 1, variant: 3 },
          { id: 24, type: LevelPieceType.Jar, x: 5, y: 3, width: 1, height: 1, variant: 3 },
          { id: 25, type: LevelPieceType.Jar, x: 0, y: 4, width: 1, height: 1, variant: 0 },
          { id: 26, type: LevelPieceType.Jar, x: 1, y: 4, width: 1, height: 1, variant: 0 },
          { id: 27, type: LevelPieceType.Jar, x: 2, y: 4, width: 1, height: 1, variant: 3 },
          { id: 28, type: LevelPieceType.Jar, x: 3, y: 4, width: 1, height: 1, variant: 3 },
          { id: 29, type: LevelPieceType.Jar, x: 4, y: 4, width: 1, height: 1, variant: 0 },
          { id: 30, type: LevelPieceType.Jar, x: 5, y: 4, width: 1, height: 1, variant: 0 },
          { id: 31, type: LevelPieceType.Jar, x: 0, y: 5, width: 1, height: 1, variant: 0 },
          { id: 32, type: LevelPieceType.Jar, x: 1, y: 5, width: 1, height: 1, variant: 0 },
          { id: 33, type: LevelPieceType.Jar, x: 2, y: 5, width: 1, height: 1, variant: 3 },
          { id: 34, type: LevelPieceType.Jar, x: 3, y: 5, width: 1, height: 1, variant: 3 },
          { id: 35, type: LevelPieceType.Jar, x: 4, y: 5, width: 1, height: 1, variant: 0 },
          { id: 36, type: LevelPieceType.Jar, x: 5, y: 5, width: 1, height: 1, variant: 0 },
        ],
      },
    ],
  },
  {
    level: 11,
    difficulty: PlayDialogDifficulty.Common,
    layers: [
      {
        grid: [
          [1, 2, 3, 4, 5, 6, 7],
          [8, 9, 10, 11, 12, 13, 14],
          [15, 16, 17, 18, 19, 20, 21],
          [22, 23, 24, 25, 26, 27, 28],
          [29, 30, 31, 32, 33, 34, 35],
          [36, 37, 38, 39, 40, 41, 42],
          [43, 44, 45, 46, 47, 48, 49]
        ],
        pieces: [
          { id: 1, type: LevelPieceType.Jar, x: 0, y: 0, width: 1, height: 1, variant: 2 },
          { id: 2, type: LevelPieceType.Cube, x: 1, y: 0, width: 1, height: 1 },
          { id: 3, type: LevelPieceType.Jar, x: 2, y: 0, width: 1, height: 1, variant: 2 },
          { id: 4, type: LevelPieceType.Cube, x: 3, y: 0, width: 1, height: 1 },
          { id: 5, type: LevelPieceType.Jar, x: 4, y: 0, width: 1, height: 1, variant: 2 },
          { id: 6, type: LevelPieceType.Cube, x: 5, y: 0, width: 1, height: 1 },
          { id: 7, type: LevelPieceType.Jar, x: 6, y: 0, width: 1, height: 1, variant: 2 },
          { id: 8, type: LevelPieceType.Cube, x: 0, y: 1, width: 1, height: 1 },
          { id: 9, type: LevelPieceType.Jar, x: 1, y: 1, width: 1, height: 1, variant: 2 },
          { id: 10, type: LevelPieceType.Cube, x: 2, y: 1, width: 1, height: 1 },
          { id: 11, type: LevelPieceType.Jar, x: 3, y: 1, width: 1, height: 1, variant: 2 },
          { id: 12, type: LevelPieceType.Cube, x: 4, y: 1, width: 1, height: 1 },
          { id: 13, type: LevelPieceType.Jar, x: 5, y: 1, width: 1, height: 1, variant: 2 },
          { id: 14, type: LevelPieceType.Cube, x: 6, y: 1, width: 1, height: 1 },
          { id: 15, type: LevelPieceType.Jar, x: 0, y: 2, width: 1, height: 1, variant: 2 },
          { id: 16, type: LevelPieceType.Cube, x: 1, y: 2, width: 1, height: 1 },
          { id: 17, type: LevelPieceType.Jar, x: 2, y: 2, width: 1, height: 1, variant: 2 },
          { id: 18, type: LevelPieceType.Cube, x: 3, y: 2, width: 1, height: 1 },
          { id: 19, type: LevelPieceType.Jar, x: 4, y: 2, width: 1, height: 1, variant: 2 },
          { id: 20, type: LevelPieceType.Cube, x: 5, y: 2, width: 1, height: 1 },
          { id: 21, type: LevelPieceType.Jar, x: 6, y: 2, width: 1, height: 1, variant: 2 },
          { id: 22, type: LevelPieceType.Cube, x: 0, y: 3, width: 1, height: 1 },
          { id: 23, type: LevelPieceType.Jar, x: 1, y: 3, width: 1, height: 1, variant: 2 },
          { id: 24, type: LevelPieceType.Cube, x: 2, y: 3, width: 1, height: 1 },
          { id: 25, type: LevelPieceType.Jar, x: 3, y: 3, width: 1, height: 1, variant: 2 },
          { id: 26, type: LevelPieceType.Cube, x: 4, y: 3, width: 1, height: 1 },
          { id: 27, type: LevelPieceType.Jar, x: 5, y: 3, width: 1, height: 1, variant: 2 },
          { id: 28, type: LevelPieceType.Cube, x: 6, y: 3, width: 1, height: 1 },
          { id: 29, type: LevelPieceType.Jar, x: 0, y: 4, width: 1, height: 1, variant: 2 },
          { id: 30, type: LevelPieceType.Cube, x: 1, y: 4, width: 1, height: 1 },
          { id: 31, type: LevelPieceType.Jar, x: 2, y: 4, width: 1, height: 1, variant: 2 },
          { id: 32, type: LevelPieceType.Cube, x: 3, y: 4, width: 1, height: 1 },
          { id: 33, type: LevelPieceType.Jar, x: 4, y: 4, width: 1, height: 1, variant: 2 },
          { id: 34, type: LevelPieceType.Cube, x: 5, y: 4, width: 1, height: 1 },
          { id: 35, type: LevelPieceType.Jar, x: 6, y: 4, width: 1, height: 1, variant: 2 },
          { id: 36, type: LevelPieceType.Cube, x: 0, y: 5, width: 1, height: 1 },
          { id: 37, type: LevelPieceType.Jar, x: 1, y: 5, width: 1, height: 1, variant: 2 },
          { id: 38, type: LevelPieceType.Cube, x: 2, y: 5, width: 1, height: 1 },
          { id: 39, type: LevelPieceType.Jar, x: 3, y: 5, width: 1, height: 1, variant: 2 },
          { id: 40, type: LevelPieceType.Cube, x: 4, y: 5, width: 1, height: 1 },
          { id: 41, type: LevelPieceType.Jar, x: 5, y: 5, width: 1, height: 1, variant: 2 },
          { id: 42, type: LevelPieceType.Cube, x: 6, y: 5, width: 1, height: 1 },
          { id: 43, type: LevelPieceType.Jar, x: 0, y: 6, width: 1, height: 1, variant: 2 },
          { id: 44, type: LevelPieceType.Cube, x: 1, y: 6, width: 1, height: 1 },
          { id: 45, type: LevelPieceType.Jar, x: 2, y: 6, width: 1, height: 1, variant: 2 },
          { id: 46, type: LevelPieceType.Cube, x: 3, y: 6, width: 1, height: 1 },
          { id: 47, type: LevelPieceType.Jar, x: 4, y: 6, width: 1, height: 1, variant: 2 },
          { id: 48, type: LevelPieceType.Cube, x: 5, y: 6, width: 1, height: 1 },
          { id: 49, type: LevelPieceType.Jar, x: 6, y: 6, width: 1, height: 1, variant: 2 },
        ],
      },
    ],
  },
  {
    level: 12,
    difficulty: PlayDialogDifficulty.Common,
    layers: [
      {
        grid: [
          [1, 2, 3, 4, 5, 6],
          [1, 7, 3, 4, 8, 6],
          [1, 9, 3, 4, 10, 6],
          [11, 11, 11, 12, 12, 12],
          [13, 14, 15, 16, 17, 18],
          [13, 19, 15, 16, 20, 18],
          [13, 21, 15, 16, 22, 18]
        ],
        pieces: [
          { id: 1, type: LevelPieceType.Cube, x: 0, y: 0, width: 1, height: 3, variant: 1 },
          { id: 2, type: LevelPieceType.Cube, x: 1, y: 0, width: 1, height: 1, variant: 0 },
          { id: 3, type: LevelPieceType.Cube, x: 2, y: 0, width: 1, height: 3, variant: 1 },
          { id: 4, type: LevelPieceType.Cube, x: 3, y: 0, width: 1, height: 3, variant: 1 },
          { id: 5, type: LevelPieceType.Cube, x: 4, y: 0, width: 1, height: 1, variant: 0 },
          { id: 6, type: LevelPieceType.Cube, x: 5, y: 0, width: 1, height: 3, variant: 1 },
          { id: 7, type: LevelPieceType.Cube, x: 1, y: 1, width: 1, height: 1, variant: 0 },
          { id: 8, type: LevelPieceType.Cube, x: 4, y: 1, width: 1, height: 1, variant: 0 },
          { id: 9, type: LevelPieceType.Cube, x: 1, y: 2, width: 1, height: 1, variant: 0 },
          { id: 10, type: LevelPieceType.Cube, x: 4, y: 2, width: 1, height: 1, variant: 0 },
          { id: 11, type: LevelPieceType.Cube, x: 0, y: 3, width: 3, height: 1, variant: 1 },
          { id: 12, type: LevelPieceType.Cube, x: 3, y: 3, width: 3, height: 1, variant: 1 },
          { id: 13, type: LevelPieceType.Cube, x: 0, y: 4, width: 1, height: 3, variant: 1 },
          { id: 14, type: LevelPieceType.Cube, x: 1, y: 4, width: 1, height: 1, variant: 0 },
          { id: 15, type: LevelPieceType.Cube, x: 2, y: 4, width: 1, height: 3, variant: 1 },
          { id: 16, type: LevelPieceType.Cube, x: 3, y: 4, width: 1, height: 3, variant: 1 },
          { id: 17, type: LevelPieceType.Cube, x: 4, y: 4, width: 1, height: 1, variant: 0 },
          { id: 18, type: LevelPieceType.Cube, x: 5, y: 4, width: 1, height: 3, variant: 1 },
          { id: 19, type: LevelPieceType.Cube, x: 1, y: 5, width: 1, height: 1, variant: 0 },
          { id: 20, type: LevelPieceType.Cube, x: 4, y: 5, width: 1, height: 1, variant: 0 },
          { id: 21, type: LevelPieceType.Cube, x: 1, y: 6, width: 1, height: 1, variant: 0 },
          { id: 22, type: LevelPieceType.Cube, x: 4, y: 6, width: 1, height: 1, variant: 0 },
        ],
      },
    ],
  },
  {
    level: 13,
    difficulty: PlayDialogDifficulty.Common,
    layers: [
      {
        grid: [
          [0, 0, 1, 1, 1, 1, 0],
          [0, 2, 2, 3, 3, 4, 4],
          [5, 6, 7, 8, 9, 10, 11],
          [12, 13, 14, 15, 16, 17, 18],
          [19, 20, 21, 22, 23, 24, 25],
          [26, 27, 28, 29, 30, 31, 32],
          [33, 34, 35, 36, 37, 38, 39],
          [40, 41, 42, 43, 44, 45, 46],
          [47, 48, 49, 50, 51, 52, 53],
          [54, 55, 56, 57, 58, 59, 60]
        ],
        pieces: [
          { id: 1, type: LevelPieceType.Cube, x: 2, y: 0, width: 4, height: 1, variant: 1 },
          { id: 2, type: LevelPieceType.Cube, x: 1, y: 1, width: 2, height: 1, variant: 1 },
          { id: 3, type: LevelPieceType.Cube, x: 3, y: 1, width: 2, height: 1, variant: 1 },
          { id: 4, type: LevelPieceType.Cube, x: 5, y: 1, width: 2, height: 1, variant: 1 },
          { id: 5, type: LevelPieceType.Cube, x: 0, y: 2, width: 1, height: 1, variant: 1 },
          { id: 6, type: LevelPieceType.Jar, x: 1, y: 2, width: 1, height: 1, variant: 0 },
          { id: 7, type: LevelPieceType.Cube, x: 2, y: 2, width: 1, height: 1, variant: 1 },
          { id: 8, type: LevelPieceType.Jar, x: 3, y: 2, width: 1, height: 1, variant: 0 },
          { id: 9, type: LevelPieceType.Cube, x: 4, y: 2, width: 1, height: 1, variant: 1 },
          { id: 10, type: LevelPieceType.Jar, x: 5, y: 2, width: 1, height: 1, variant: 0 },
          { id: 11, type: LevelPieceType.Cube, x: 6, y: 2, width: 1, height: 1, variant: 1 },
          { id: 12, type: LevelPieceType.Cube, x: 0, y: 3, width: 1, height: 1, variant: 1, offsetX: 0.5 },
          { id: 13, type: LevelPieceType.Jar, x: 1, y: 3, width: 1, height: 1, variant: 3, offsetX: 0.5 },
          { id: 14, type: LevelPieceType.Cube, x: 2, y: 3, width: 1, height: 1, variant: 1, offsetX: 0.5 },
          { id: 15, type: LevelPieceType.Jar, x: 3, y: 3, width: 1, height: 1, variant: 3, offsetX: 0.5 },
          { id: 16, type: LevelPieceType.Cube, x: 4, y: 3, width: 1, height: 1, variant: 1, offsetX: 0.5 },
          { id: 17, type: LevelPieceType.Jar, x: 5, y: 3, width: 1, height: 1, variant: 3, offsetX: 0.5 },
          { id: 18, type: LevelPieceType.Cube, x: 6, y: 3, width: 1, height: 1, variant: 1, offsetX: 0.5 },
          { id: 19, type: LevelPieceType.Cube, x: 0, y: 4, width: 1, height: 1, variant: 1 },
          { id: 20, type: LevelPieceType.Jar, x: 1, y: 4, width: 1, height: 1, variant: 1 },
          { id: 21, type: LevelPieceType.Cube, x: 2, y: 4, width: 1, height: 1, variant: 1 },
          { id: 22, type: LevelPieceType.Jar, x: 3, y: 4, width: 1, height: 1, variant: 1 },
          { id: 23, type: LevelPieceType.Cube, x: 4, y: 4, width: 1, height: 1, variant: 1 },
          { id: 24, type: LevelPieceType.Jar, x: 5, y: 4, width: 1, height: 1, variant: 1 },
          { id: 25, type: LevelPieceType.Cube, x: 6, y: 4, width: 1, height: 1, variant: 1 },
          { id: 26, type: LevelPieceType.Cube, x: 0, y: 5, width: 1, height: 1, variant: 1, offsetX: 0.5 },
          { id: 27, type: LevelPieceType.Jar, x: 1, y: 5, width: 1, height: 1, variant: 2, offsetX: 0.5 },
          { id: 28, type: LevelPieceType.Cube, x: 2, y: 5, width: 1, height: 1, variant: 1, offsetX: 0.5 },
          { id: 29, type: LevelPieceType.Jar, x: 3, y: 5, width: 1, height: 1, variant: 2, offsetX: 0.5 },
          { id: 30, type: LevelPieceType.Cube, x: 4, y: 5, width: 1, height: 1, variant: 1, offsetX: 0.5 },
          { id: 31, type: LevelPieceType.Jar, x: 5, y: 5, width: 1, height: 1, variant: 2, offsetX: 0.5 },
          { id: 32, type: LevelPieceType.Cube, x: 6, y: 5, width: 1, height: 1, variant: 1, offsetX: 0.5 },
          { id: 33, type: LevelPieceType.Cube, x: 0, y: 6, width: 1, height: 1, variant: 1 },
          { id: 34, type: LevelPieceType.Jar, x: 1, y: 6, width: 1, height: 1, variant: 0 },
          { id: 35, type: LevelPieceType.Cube, x: 2, y: 6, width: 1, height: 1, variant: 1 },
          { id: 36, type: LevelPieceType.Jar, x: 3, y: 6, width: 1, height: 1, variant: 0 },
          { id: 37, type: LevelPieceType.Cube, x: 4, y: 6, width: 1, height: 1, variant: 1 },
          { id: 38, type: LevelPieceType.Jar, x: 5, y: 6, width: 1, height: 1, variant: 0 },
          { id: 39, type: LevelPieceType.Cube, x: 6, y: 6, width: 1, height: 1, variant: 1 },
          { id: 40, type: LevelPieceType.Cube, x: 0, y: 7, width: 1, height: 1, variant: 1, offsetX: 0.5 },
          { id: 41, type: LevelPieceType.Jar, x: 1, y: 7, width: 1, height: 1, variant: 1, offsetX: 0.5 },
          { id: 42, type: LevelPieceType.Cube, x: 2, y: 7, width: 1, height: 1, variant: 1, offsetX: 0.5 },
          { id: 43, type: LevelPieceType.Jar, x: 3, y: 7, width: 1, height: 1, variant: 1, offsetX: 0.5 },
          { id: 44, type: LevelPieceType.Cube, x: 4, y: 7, width: 1, height: 1, variant: 1, offsetX: 0.5 },
          { id: 45, type: LevelPieceType.Jar, x: 5, y: 7, width: 1, height: 1, variant: 1, offsetX: 0.5 },
          { id: 46, type: LevelPieceType.Cube, x: 6, y: 7, width: 1, height: 1, variant: 1, offsetX: 0.5 },
          { id: 47, type: LevelPieceType.Cube, x: 0, y: 8, width: 1, height: 1, variant: 1 },
          { id: 48, type: LevelPieceType.Jar, x: 1, y: 8, width: 1, height: 1, variant: 3 },
          { id: 49, type: LevelPieceType.Cube, x: 2, y: 8, width: 1, height: 1, variant: 1 },
          { id: 50, type: LevelPieceType.Jar, x: 3, y: 8, width: 1, height: 1, variant: 3 },
          { id: 51, type: LevelPieceType.Cube, x: 4, y: 8, width: 1, height: 1, variant: 1 },
          { id: 52, type: LevelPieceType.Jar, x: 5, y: 8, width: 1, height: 1, variant: 3 },
          { id: 53, type: LevelPieceType.Cube, x: 6, y: 8, width: 1, height: 1, variant: 1 },
          { id: 54, type: LevelPieceType.Cube, x: 0, y: 9, width: 1, height: 1, variant: 1, offsetX: 0.5 },
          { id: 55, type: LevelPieceType.Jar, x: 1, y: 9, width: 1, height: 1, variant: 2, offsetX: 0.5 },
          { id: 56, type: LevelPieceType.Cube, x: 2, y: 9, width: 1, height: 1, variant: 1, offsetX: 0.5 },
          { id: 57, type: LevelPieceType.Jar, x: 3, y: 9, width: 1, height: 1, variant: 2, offsetX: 0.5 },
          { id: 58, type: LevelPieceType.Cube, x: 4, y: 9, width: 1, height: 1, variant: 1, offsetX: 0.5 },
          { id: 59, type: LevelPieceType.Jar, x: 5, y: 9, width: 1, height: 1, variant: 2, offsetX: 0.5 },
          { id: 60, type: LevelPieceType.Cube, x: 6, y: 9, width: 1, height: 1, variant: 1, offsetX: 0.5 },
        ],
      },
    ],
  },
  {
    level: 14,
    difficulty: PlayDialogDifficulty.Common,
    layers: [
      {
        grid: [
          [1, 2, 3, 4, 5, 6],
          [7, 8, 9, 10, 11, 12],
          [13, 14, 15, 16, 17, 18],
          [19, 20, 21, 22, 23, 24],
          [25, 26, 27, 28, 29, 30],
          [31, 32, 33, 34, 35, 36],
          [37, 38, 39, 40, 41, 42],
          [43, 44, 45, 46, 47, 48]
        ],
        pieces: [
          { id: 1, type: LevelPieceType.Cube, x: 0, y: 0, width: 1, height: 1, variant: 1 },
          { id: 2, type: LevelPieceType.Cube, x: 1, y: 0, width: 1, height: 1, variant: 1 },
          { id: 3, type: LevelPieceType.Cube, x: 2, y: 0, width: 1, height: 1, variant: 1 },
          { id: 4, type: LevelPieceType.Cube, x: 3, y: 0, width: 1, height: 1, variant: 1 },
          { id: 5, type: LevelPieceType.Cube, x: 4, y: 0, width: 1, height: 1, variant: 1 },
          { id: 6, type: LevelPieceType.Cube, x: 5, y: 0, width: 1, height: 1, variant: 1 },
          { id: 7, type: LevelPieceType.Cube, x: 0, y: 1, width: 1, height: 1, variant: 0 },
          { id: 8, type: LevelPieceType.Cube, x: 1, y: 1, width: 1, height: 1, variant: 0 },
          { id: 9, type: LevelPieceType.Cube, x: 2, y: 1, width: 1, height: 1, variant: 0 },
          { id: 10, type: LevelPieceType.Cube, x: 3, y: 1, width: 1, height: 1, variant: 0 },
          { id: 11, type: LevelPieceType.Cube, x: 4, y: 1, width: 1, height: 1, variant: 0 },
          { id: 12, type: LevelPieceType.Cube, x: 5, y: 1, width: 1, height: 1, variant: 0 },
          { id: 13, type: LevelPieceType.Cube, x: 0, y: 2, width: 1, height: 1, variant: 1 },
          { id: 14, type: LevelPieceType.Cube, x: 1, y: 2, width: 1, height: 1, variant: 1 },
          { id: 15, type: LevelPieceType.Cube, x: 2, y: 2, width: 1, height: 1, variant: 1 },
          { id: 16, type: LevelPieceType.Cube, x: 3, y: 2, width: 1, height: 1, variant: 1 },
          { id: 17, type: LevelPieceType.Cube, x: 4, y: 2, width: 1, height: 1, variant: 1 },
          { id: 18, type: LevelPieceType.Cube, x: 5, y: 2, width: 1, height: 1, variant: 1 },
          { id: 19, type: LevelPieceType.Cube, x: 0, y: 3, width: 1, height: 1, variant: 0 },
          { id: 20, type: LevelPieceType.Cube, x: 1, y: 3, width: 1, height: 1, variant: 0 },
          { id: 21, type: LevelPieceType.Cube, x: 2, y: 3, width: 1, height: 1, variant: 0 },
          { id: 22, type: LevelPieceType.Cube, x: 3, y: 3, width: 1, height: 1, variant: 0 },
          { id: 23, type: LevelPieceType.Cube, x: 4, y: 3, width: 1, height: 1, variant: 0 },
          { id: 24, type: LevelPieceType.Cube, x: 5, y: 3, width: 1, height: 1, variant: 0 },
          { id: 25, type: LevelPieceType.Cube, x: 0, y: 4, width: 1, height: 1, variant: 1 },
          { id: 26, type: LevelPieceType.Cube, x: 1, y: 4, width: 1, height: 1, variant: 1 },
          { id: 27, type: LevelPieceType.Cube, x: 2, y: 4, width: 1, height: 1, variant: 1 },
          { id: 28, type: LevelPieceType.Cube, x: 3, y: 4, width: 1, height: 1, variant: 1 },
          { id: 29, type: LevelPieceType.Cube, x: 4, y: 4, width: 1, height: 1, variant: 1 },
          { id: 30, type: LevelPieceType.Cube, x: 5, y: 4, width: 1, height: 1, variant: 1 },
          { id: 31, type: LevelPieceType.Cube, x: 0, y: 5, width: 1, height: 1, variant: 0 },
          { id: 32, type: LevelPieceType.Cube, x: 1, y: 5, width: 1, height: 1, variant: 0 },
          { id: 33, type: LevelPieceType.Cube, x: 2, y: 5, width: 1, height: 1, variant: 0 },
          { id: 34, type: LevelPieceType.Cube, x: 3, y: 5, width: 1, height: 1, variant: 0 },
          { id: 35, type: LevelPieceType.Cube, x: 4, y: 5, width: 1, height: 1, variant: 0 },
          { id: 36, type: LevelPieceType.Cube, x: 5, y: 5, width: 1, height: 1, variant: 0 },
          { id: 37, type: LevelPieceType.Cube, x: 0, y: 6, width: 1, height: 1, variant: 1 },
          { id: 38, type: LevelPieceType.Cube, x: 1, y: 6, width: 1, height: 1, variant: 1 },
          { id: 39, type: LevelPieceType.Cube, x: 2, y: 6, width: 1, height: 1, variant: 1 },
          { id: 40, type: LevelPieceType.Cube, x: 3, y: 6, width: 1, height: 1, variant: 1 },
          { id: 41, type: LevelPieceType.Cube, x: 4, y: 6, width: 1, height: 1, variant: 1 },
          { id: 42, type: LevelPieceType.Cube, x: 5, y: 6, width: 1, height: 1, variant: 1 },
          { id: 43, type: LevelPieceType.Cube, x: 0, y: 7, width: 1, height: 1, variant: 0 },
          { id: 44, type: LevelPieceType.Cube, x: 1, y: 7, width: 1, height: 1, variant: 0 },
          { id: 45, type: LevelPieceType.Cube, x: 2, y: 7, width: 1, height: 1, variant: 0 },
          { id: 46, type: LevelPieceType.Cube, x: 3, y: 7, width: 1, height: 1, variant: 0 },
          { id: 47, type: LevelPieceType.Cube, x: 4, y: 7, width: 1, height: 1, variant: 0 },
          { id: 48, type: LevelPieceType.Cube, x: 5, y: 7, width: 1, height: 1, variant: 0 },
        ],
      },
    ],
  },
{
  "level": 16,
  difficulty: PlayDialogDifficulty.Common,
  "layers": [
    {
      "grid": [
        [1, 2, 3, 4, 5, 0],
        [6, 7, 8, 9, 10, 0],
        [11, 12, 13, 14, 15, 0],
        [16, 17, 18, 19, 20, 0],
        [21, 22, 23, 24, 25, 0],
        [26, 26, 27, 27, 28, 28],
        [29, 0, 30, 0, 31, 0],
        [29, 0, 30, 0, 31, 0]
      ],
      "pieces": [
        { "id": 1, "type": 4, "x": 0, "y": 0, "width": 1, "height": 1, "variant": 1, "offsetX": 0.5 },
        { "id": 2, "type": 4, "x": 1, "y": 0, "width": 1, "height": 1, "variant": 1, "offsetX": 0.5 },
        { "id": 3, "type": 4, "x": 2, "y": 0, "width": 1, "height": 1, "variant": 1, "offsetX": 0.5 },
        { "id": 4, "type": 4, "x": 3, "y": 0, "width": 1, "height": 1, "variant": 1, "offsetX": 0.5 },
        { "id": 5, "type": 4, "x": 4, "y": 0, "width": 1, "height": 1, "variant": 1, "offsetX": 0.5 },
        { "id": 6, "type": 3, "x": 0, "y": 1, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 7, "type": 3, "x": 1, "y": 1, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 8, "type": 3, "x": 2, "y": 1, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 9, "type": 3, "x": 3, "y": 1, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 10, "type": 3, "x": 4, "y": 1, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 11, "type": 3, "x": 0, "y": 2, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 12, "type": 3, "x": 1, "y": 2, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 13, "type": 3, "x": 2, "y": 2, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 14, "type": 3, "x": 3, "y": 2, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 15, "type": 3, "x": 4, "y": 2, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 16, "type": 1, "x": 0, "y": 3, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 17, "type": 1, "x": 1, "y": 3, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 18, "type": 1, "x": 2, "y": 3, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 19, "type": 1, "x": 3, "y": 3, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 20, "type": 1, "x": 4, "y": 3, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 21, "type": 1, "x": 0, "y": 4, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 22, "type": 1, "x": 1, "y": 4, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 23, "type": 1, "x": 2, "y": 4, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 24, "type": 1, "x": 3, "y": 4, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 25, "type": 1, "x": 4, "y": 4, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 26, "type": 1, "x": 0, "y": 5, "width": 2, "height": 1, "variant": 1 },
        { "id": 27, "type": 1, "x": 2, "y": 5, "width": 2, "height": 1, "variant": 1 },
        { "id": 28, "type": 1, "x": 4, "y": 5, "width": 2, "height": 1, "variant": 1 },
        { "id": 29, "type": 1, "x": 0, "y": 6, "width": 1, "height": 2, "variant": 1, "offsetX": 0.5 },
        { "id": 30, "type": 1, "x": 2, "y": 6, "width": 1, "height": 2, "variant": 1, "offsetX": 0.5 },
        { "id": 31, "type": 1, "x": 4, "y": 6, "width": 1, "height": 2, "variant": 1, "offsetX": 0.5 }
      ]
    },
    {
      "grid": [
        [0, 0, 0, 0, 0, 0],
        [0, 0, 0, 0, 0, 0],
        [0, 0, 0, 0, 0, 0],
        [0, 0, 0, 0, 0, 0],
        [0, 0, 0, 0, 0, 0],
        [0, 0, 0, 0, 0, 0]
      ],
      "pieces": [

      ]
    }
  ]
},
{
  "level": 17,
  difficulty: PlayDialogDifficulty.Common,
  "layers": [
    {
      "grid": [
        [0, 0, 1, 0, 0, 0],
        [0, 0, 2, 3, 0, 0],
        [0, 4, 5, 6, 0, 0],
        [0, 7, 8, 9, 10, 0],
        [0, 11, 12, 13, 14, 0],
        [15, 16, 17, 18, 19, 0],
        [20, 21, 22, 23, 24, 0],
        [25, 26, 27, 28, 29, 30],
        [31, 32, 33, 34, 35, 36]
      ],
      "pieces": [
        { "id": 1, "type": 1, "x": 2, "y": 0, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 2, "type": 1, "x": 2, "y": 1, "width": 1, "height": 1, "variant": 1 },
        { "id": 3, "type": 1, "x": 3, "y": 1, "width": 1, "height": 1, "variant": 1 },
        { "id": 4, "type": 1, "x": 1, "y": 2, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 5, "type": 1, "x": 2, "y": 2, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 6, "type": 1, "x": 3, "y": 2, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 7, "type": 1, "x": 1, "y": 3, "width": 1, "height": 1, "variant": 1 },
        { "id": 8, "type": 1, "x": 2, "y": 3, "width": 1, "height": 1, "variant": 1 },
        { "id": 9, "type": 1, "x": 3, "y": 3, "width": 1, "height": 1, "variant": 1 },
        { "id": 10, "type": 1, "x": 4, "y": 3, "width": 1, "height": 1, "variant": 1 },
        { "id": 11, "type": 1, "x": 1, "y": 4, "width": 1, "height": 1, "variant": 1 },
        { "id": 12, "type": 1, "x": 2, "y": 4, "width": 1, "height": 1, "variant": 1 },
        { "id": 13, "type": 1, "x": 3, "y": 4, "width": 1, "height": 1, "variant": 1 },
        { "id": 14, "type": 1, "x": 4, "y": 4, "width": 1, "height": 1, "variant": 1 },
        { "id": 15, "type": 1, "x": 0, "y": 5, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 16, "type": 1, "x": 1, "y": 5, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 17, "type": 1, "x": 2, "y": 5, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 18, "type": 1, "x": 3, "y": 5, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 19, "type": 1, "x": 4, "y": 5, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 20, "type": 1, "x": 0, "y": 6, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 21, "type": 1, "x": 1, "y": 6, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 22, "type": 1, "x": 2, "y": 6, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 23, "type": 1, "x": 3, "y": 6, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 24, "type": 1, "x": 4, "y": 6, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 25, "type": 1, "x": 0, "y": 7, "width": 1, "height": 1, "variant": 1 },
        { "id": 26, "type": 1, "x": 1, "y": 7, "width": 1, "height": 1, "variant": 1 },
        { "id": 27, "type": 1, "x": 2, "y": 7, "width": 1, "height": 1, "variant": 1 },
        { "id": 28, "type": 1, "x": 3, "y": 7, "width": 1, "height": 1, "variant": 1 },
        { "id": 29, "type": 1, "x": 4, "y": 7, "width": 1, "height": 1, "variant": 1 },
        { "id": 30, "type": 1, "x": 5, "y": 7, "width": 1, "height": 1, "variant": 1 },
        { "id": 31, "type": 1, "x": 0, "y": 8, "width": 1, "height": 1, "variant": 1 },
        { "id": 32, "type": 1, "x": 1, "y": 8, "width": 1, "height": 1, "variant": 1 },
        { "id": 33, "type": 1, "x": 2, "y": 8, "width": 1, "height": 1, "variant": 1 },
        { "id": 34, "type": 1, "x": 3, "y": 8, "width": 1, "height": 1, "variant": 1 },
        { "id": 35, "type": 1, "x": 4, "y": 8, "width": 1, "height": 1, "variant": 1 },
        { "id": 36, "type": 1, "x": 5, "y": 8, "width": 1, "height": 1, "variant": 1 }
      ]
    }
  ]
},
{
  "level": 18,
  difficulty: PlayDialogDifficulty.Common,
  "layers": [
    {
      "grid": [
        [1, 1, 1, 1, 2, 2, 2, 2],
        [3, 3, 3, 3, 4, 4, 4, 4],
        [5, 5, 5, 5, 6, 6, 6, 6],
        [7, 8, 9, 10, 11, 12, 13, 0],
        [14, 15, 16, 17, 18, 19, 20, 0],
        [21, 22, 23, 24, 25, 26, 27, 0],
        [28, 29, 30, 31, 32, 33, 34, 0],
        [35, 36, 37, 38, 39, 40, 41, 0],
        [42, 43, 44, 45, 46, 47, 48, 0]
      ],
      "pieces": [
        { "id": 1, "type": 1, "x": 0, "y": 0, "width": 4, "height": 1, "variant": 1 },
        { "id": 2, "type": 1, "x": 4, "y": 0, "width": 4, "height": 1, "variant": 1 },
        { "id": 3, "type": 1, "x": 0, "y": 1, "width": 4, "height": 1, "variant": 1 },
        { "id": 4, "type": 1, "x": 4, "y": 1, "width": 4, "height": 1, "variant": 1 },
        { "id": 5, "type": 1, "x": 0, "y": 2, "width": 4, "height": 1, "variant": 1 },
        { "id": 6, "type": 1, "x": 4, "y": 2, "width": 4, "height": 1, "variant": 1 },
        { "id": 7, "type": 3, "x": 0, "y": 3, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 8, "type": 3, "x": 1, "y": 3, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 9, "type": 3, "x": 2, "y": 3, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 10, "type": 3, "x": 3, "y": 3, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 11, "type": 3, "x": 4, "y": 3, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 12, "type": 3, "x": 5, "y": 3, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 13, "type": 3, "x": 6, "y": 3, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 14, "type": 3, "x": 0, "y": 4, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 15, "type": 3, "x": 1, "y": 4, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 16, "type": 3, "x": 2, "y": 4, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 17, "type": 3, "x": 3, "y": 4, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 18, "type": 3, "x": 4, "y": 4, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 19, "type": 3, "x": 5, "y": 4, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 20, "type": 3, "x": 6, "y": 4, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 21, "type": 3, "x": 0, "y": 5, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 22, "type": 3, "x": 1, "y": 5, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 23, "type": 3, "x": 2, "y": 5, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 24, "type": 3, "x": 3, "y": 5, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 25, "type": 3, "x": 4, "y": 5, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 26, "type": 3, "x": 5, "y": 5, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 27, "type": 3, "x": 6, "y": 5, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 28, "type": 1, "x": 0, "y": 6, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 29, "type": 1, "x": 1, "y": 6, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 30, "type": 1, "x": 2, "y": 6, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 31, "type": 1, "x": 3, "y": 6, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 32, "type": 1, "x": 4, "y": 6, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 33, "type": 1, "x": 5, "y": 6, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 34, "type": 1, "x": 6, "y": 6, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 35, "type": 1, "x": 0, "y": 7, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 36, "type": 1, "x": 1, "y": 7, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 37, "type": 1, "x": 2, "y": 7, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 38, "type": 1, "x": 3, "y": 7, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 39, "type": 1, "x": 4, "y": 7, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 40, "type": 1, "x": 5, "y": 7, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 41, "type": 1, "x": 6, "y": 7, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 42, "type": 1, "x": 0, "y": 8, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 43, "type": 1, "x": 1, "y": 8, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 44, "type": 1, "x": 2, "y": 8, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 45, "type": 1, "x": 3, "y": 8, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 46, "type": 1, "x": 4, "y": 8, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 47, "type": 1, "x": 5, "y": 8, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 48, "type": 1, "x": 6, "y": 8, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 }
      ]
    }
  ]
},
{
  "level": 19,
  difficulty: PlayDialogDifficulty.Common,
  "layers": [
    {
      "grid": [
        [1, 2, 3, 4, 5, 6, 7, 8],
        [9, 10, 11, 12, 13, 14, 15, 16],
        [17, 18, 19, 20, 21, 22, 23, 24],
        [25, 26, 27, 28, 29, 30, 31, 32],
        [33, 34, 35, 36, 37, 38, 39, 40],
        [41, 42, 43, 44, 45, 46, 47, 48],
        [49, 50, 51, 52, 53, 54, 55, 56]
      ],
      "pieces": [
        { "id": 1, "type": 1, "x": 0, "y": 0, "width": 1, "height": 1 },
        { "id": 2, "type": 1, "x": 1, "y": 0, "width": 1, "height": 1 },
        { "id": 3, "type": 1, "x": 2, "y": 0, "width": 1, "height": 1 },
        { "id": 4, "type": 1, "x": 3, "y": 0, "width": 1, "height": 1 },
        { "id": 5, "type": 1, "x": 4, "y": 0, "width": 1, "height": 1 },
        { "id": 6, "type": 1, "x": 5, "y": 0, "width": 1, "height": 1 },
        { "id": 7, "type": 1, "x": 6, "y": 0, "width": 1, "height": 1 },
        { "id": 8, "type": 1, "x": 7, "y": 0, "width": 1, "height": 1 },
        { "id": 9, "type": 1, "x": 0, "y": 1, "width": 1, "height": 1 },
        { "id": 10, "type": 1, "x": 1, "y": 1, "width": 1, "height": 1 },
        { "id": 11, "type": 1, "x": 2, "y": 1, "width": 1, "height": 1 },
        { "id": 12, "type": 1, "x": 3, "y": 1, "width": 1, "height": 1 },
        { "id": 13, "type": 1, "x": 4, "y": 1, "width": 1, "height": 1 },
        { "id": 14, "type": 1, "x": 5, "y": 1, "width": 1, "height": 1 },
        { "id": 15, "type": 1, "x": 6, "y": 1, "width": 1, "height": 1 },
        { "id": 16, "type": 1, "x": 7, "y": 1, "width": 1, "height": 1 },
        { "id": 17, "type": 1, "x": 0, "y": 2, "width": 1, "height": 1 },
        { "id": 18, "type": 1, "x": 1, "y": 2, "width": 1, "height": 1 },
        { "id": 19, "type": 1, "x": 2, "y": 2, "width": 1, "height": 1 },
        { "id": 20, "type": 1, "x": 3, "y": 2, "width": 1, "height": 1 },
        { "id": 21, "type": 1, "x": 4, "y": 2, "width": 1, "height": 1 },
        { "id": 22, "type": 1, "x": 5, "y": 2, "width": 1, "height": 1 },
        { "id": 23, "type": 1, "x": 6, "y": 2, "width": 1, "height": 1 },
        { "id": 24, "type": 1, "x": 7, "y": 2, "width": 1, "height": 1 },
        { "id": 25, "type": 1, "x": 0, "y": 3, "width": 1, "height": 1 },
        { "id": 26, "type": 1, "x": 1, "y": 3, "width": 1, "height": 1 },
        { "id": 27, "type": 1, "x": 2, "y": 3, "width": 1, "height": 1 },
        { "id": 28, "type": 1, "x": 3, "y": 3, "width": 1, "height": 1 },
        { "id": 29, "type": 1, "x": 4, "y": 3, "width": 1, "height": 1 },
        { "id": 30, "type": 1, "x": 5, "y": 3, "width": 1, "height": 1 },
        { "id": 31, "type": 1, "x": 6, "y": 3, "width": 1, "height": 1 },
        { "id": 32, "type": 1, "x": 7, "y": 3, "width": 1, "height": 1 },
        { "id": 33, "type": 1, "x": 0, "y": 4, "width": 1, "height": 1 },
        { "id": 34, "type": 1, "x": 1, "y": 4, "width": 1, "height": 1 },
        { "id": 35, "type": 1, "x": 2, "y": 4, "width": 1, "height": 1 },
        { "id": 36, "type": 1, "x": 3, "y": 4, "width": 1, "height": 1 },
        { "id": 37, "type": 1, "x": 4, "y": 4, "width": 1, "height": 1 },
        { "id": 38, "type": 1, "x": 5, "y": 4, "width": 1, "height": 1 },
        { "id": 39, "type": 1, "x": 6, "y": 4, "width": 1, "height": 1 },
        { "id": 40, "type": 1, "x": 7, "y": 4, "width": 1, "height": 1 },
        { "id": 41, "type": 1, "x": 0, "y": 5, "width": 1, "height": 1 },
        { "id": 42, "type": 1, "x": 1, "y": 5, "width": 1, "height": 1 },
        { "id": 43, "type": 1, "x": 2, "y": 5, "width": 1, "height": 1 },
        { "id": 44, "type": 1, "x": 3, "y": 5, "width": 1, "height": 1 },
        { "id": 45, "type": 1, "x": 4, "y": 5, "width": 1, "height": 1 },
        { "id": 46, "type": 1, "x": 5, "y": 5, "width": 1, "height": 1 },
        { "id": 47, "type": 1, "x": 6, "y": 5, "width": 1, "height": 1 },
        { "id": 48, "type": 1, "x": 7, "y": 5, "width": 1, "height": 1 },
        { "id": 49, "type": 1, "x": 0, "y": 6, "width": 1, "height": 1 },
        { "id": 50, "type": 1, "x": 1, "y": 6, "width": 1, "height": 1 },
        { "id": 51, "type": 1, "x": 2, "y": 6, "width": 1, "height": 1 },
        { "id": 52, "type": 1, "x": 3, "y": 6, "width": 1, "height": 1 },
        { "id": 53, "type": 1, "x": 4, "y": 6, "width": 1, "height": 1 },
        { "id": 54, "type": 1, "x": 5, "y": 6, "width": 1, "height": 1 },
        { "id": 55, "type": 1, "x": 6, "y": 6, "width": 1, "height": 1 },
        { "id": 56, "type": 1, "x": 7, "y": 6, "width": 1, "height": 1 }
      ]
    }
  ]
},
{
  "level": 20,
  difficulty: PlayDialogDifficulty.Common,
  "layers": [
    {
      "grid": [
        [1, 2, 3, 4, 5, 6, 7, 8, 9],
        [10, 11, 12, 13, 14, 15, 16, 17, 18],
        [19, 20, 21, 22, 23, 24, 25, 26, 27],
        [28, 29, 30, 31, 32, 33, 34, 35, 36],
        [37, 38, 39, 40, 41, 42, 43, 44, 45],
        [46, 47, 48, 49, 50, 51, 52, 53, 54],
        [55, 56, 57, 58, 59, 60, 61, 62, 63]
      ],
      "pieces": [
        { "id": 1, "type": 1, "x": 0, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 2, "type": 1, "x": 1, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 3, "type": 1, "x": 2, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 4, "type": 1, "x": 3, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 5, "type": 1, "x": 4, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 6, "type": 1, "x": 5, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 7, "type": 1, "x": 6, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 8, "type": 1, "x": 7, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 9, "type": 1, "x": 8, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 10, "type": 1, "x": 0, "y": 1, "width": 1, "height": 1, "variant": 1 },
        { "id": 11, "type": 1, "x": 1, "y": 1, "width": 1, "height": 1, "variant": 0 },
        { "id": 12, "type": 1, "x": 2, "y": 1, "width": 1, "height": 1, "variant": 0 },
        { "id": 13, "type": 1, "x": 3, "y": 1, "width": 1, "height": 1, "variant": 0 },
        { "id": 14, "type": 1, "x": 4, "y": 1, "width": 1, "height": 1, "variant": 1 },
        { "id": 15, "type": 1, "x": 5, "y": 1, "width": 1, "height": 1, "variant": 0 },
        { "id": 16, "type": 1, "x": 6, "y": 1, "width": 1, "height": 1, "variant": 0 },
        { "id": 17, "type": 1, "x": 7, "y": 1, "width": 1, "height": 1, "variant": 0 },
        { "id": 18, "type": 1, "x": 8, "y": 1, "width": 1, "height": 1, "variant": 1 },
        { "id": 19, "type": 1, "x": 0, "y": 2, "width": 1, "height": 1, "variant": 1 },
        { "id": 20, "type": 1, "x": 1, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 21, "type": 1, "x": 2, "y": 2, "width": 1, "height": 1, "variant": 1 },
        { "id": 22, "type": 1, "x": 3, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 23, "type": 1, "x": 4, "y": 2, "width": 1, "height": 1, "variant": 1 },
        { "id": 24, "type": 1, "x": 5, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 25, "type": 1, "x": 6, "y": 2, "width": 1, "height": 1, "variant": 1 },
        { "id": 26, "type": 1, "x": 7, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 27, "type": 1, "x": 8, "y": 2, "width": 1, "height": 1, "variant": 1 },
        { "id": 28, "type": 1, "x": 0, "y": 3, "width": 1, "height": 1, "variant": 1 },
        { "id": 29, "type": 1, "x": 1, "y": 3, "width": 1, "height": 1, "variant": 0 },
        { "id": 30, "type": 1, "x": 2, "y": 3, "width": 1, "height": 1, "variant": 1 },
        { "id": 31, "type": 1, "x": 3, "y": 3, "width": 1, "height": 1, "variant": 0 },
        { "id": 32, "type": 1, "x": 4, "y": 3, "width": 1, "height": 1, "variant": 1 },
        { "id": 33, "type": 1, "x": 5, "y": 3, "width": 1, "height": 1, "variant": 0 },
        { "id": 34, "type": 1, "x": 6, "y": 3, "width": 1, "height": 1, "variant": 1 },
        { "id": 35, "type": 1, "x": 7, "y": 3, "width": 1, "height": 1, "variant": 0 },
        { "id": 36, "type": 1, "x": 8, "y": 3, "width": 1, "height": 1, "variant": 1 },
        { "id": 37, "type": 1, "x": 0, "y": 4, "width": 1, "height": 1, "variant": 1 },
        { "id": 38, "type": 1, "x": 1, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 39, "type": 1, "x": 2, "y": 4, "width": 1, "height": 1, "variant": 1 },
        { "id": 40, "type": 1, "x": 3, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 41, "type": 1, "x": 4, "y": 4, "width": 1, "height": 1, "variant": 1 },
        { "id": 42, "type": 1, "x": 5, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 43, "type": 1, "x": 6, "y": 4, "width": 1, "height": 1, "variant": 1 },
        { "id": 44, "type": 1, "x": 7, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 45, "type": 1, "x": 8, "y": 4, "width": 1, "height": 1, "variant": 1 },
        { "id": 46, "type": 1, "x": 0, "y": 5, "width": 1, "height": 1, "variant": 1 },
        { "id": 47, "type": 1, "x": 1, "y": 5, "width": 1, "height": 1, "variant": 0 },
        { "id": 48, "type": 1, "x": 2, "y": 5, "width": 1, "height": 1, "variant": 0 },
        { "id": 49, "type": 1, "x": 3, "y": 5, "width": 1, "height": 1, "variant": 0 },
        { "id": 50, "type": 1, "x": 4, "y": 5, "width": 1, "height": 1, "variant": 1 },
        { "id": 51, "type": 1, "x": 5, "y": 5, "width": 1, "height": 1, "variant": 0 },
        { "id": 52, "type": 1, "x": 6, "y": 5, "width": 1, "height": 1, "variant": 0 },
        { "id": 53, "type": 1, "x": 7, "y": 5, "width": 1, "height": 1, "variant": 0 },
        { "id": 54, "type": 1, "x": 8, "y": 5, "width": 1, "height": 1, "variant": 1 },
        { "id": 55, "type": 1, "x": 0, "y": 6, "width": 1, "height": 1, "variant": 1 },
        { "id": 56, "type": 1, "x": 1, "y": 6, "width": 1, "height": 1, "variant": 1 },
        { "id": 57, "type": 1, "x": 2, "y": 6, "width": 1, "height": 1, "variant": 1 },
        { "id": 58, "type": 1, "x": 3, "y": 6, "width": 1, "height": 1, "variant": 1 },
        { "id": 59, "type": 1, "x": 4, "y": 6, "width": 1, "height": 1, "variant": 1 },
        { "id": 60, "type": 1, "x": 5, "y": 6, "width": 1, "height": 1, "variant": 1 },
        { "id": 61, "type": 1, "x": 6, "y": 6, "width": 1, "height": 1, "variant": 1 },
        { "id": 62, "type": 1, "x": 7, "y": 6, "width": 1, "height": 1, "variant": 1 },
        { "id": 63, "type": 1, "x": 8, "y": 6, "width": 1, "height": 1, "variant": 1 }
      ]
    }
  ]
},
{
  "level": 21,
  difficulty: PlayDialogDifficulty.Common,
  "layers": [
    {
      "grid": [
        [1, 2, 3, 4, 5, 6, 7],
        [1, 8, 9, 10, 11, 12, 7],
        [13, 8, 9, 10, 11, 12, 14],
        [13, 15, 16, 17, 18, 19, 14],
        [20, 15, 21, 22, 23, 19, 24],
        [20, 25, 21, 26, 23, 27, 24],
        [28, 25, 29, 26, 30, 27, 31]
      ],
      "pieces": [
        { "id": 1, "type": 3, "x": 0, "y": 0, "width": 1, "height": 2, "variant": 1 },
        { "id": 2, "type": 3, "x": 1, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 3, "type": 3, "x": 2, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 4, "type": 3, "x": 3, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 5, "type": 3, "x": 4, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 6, "type": 3, "x": 5, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 7, "type": 3, "x": 6, "y": 0, "width": 1, "height": 2, "variant": 1 },
        { "id": 8, "type": 3, "x": 1, "y": 1, "width": 1, "height": 2, "variant": 1 },
        { "id": 9, "type": 3, "x": 2, "y": 1, "width": 1, "height": 2, "variant": 1 },
        { "id": 10, "type": 3, "x": 3, "y": 1, "width": 1, "height": 2, "variant": 1 },
        { "id": 11, "type": 3, "x": 4, "y": 1, "width": 1, "height": 2, "variant": 1 },
        { "id": 12, "type": 3, "x": 5, "y": 1, "width": 1, "height": 2, "variant": 1 },
        { "id": 13, "type": 3, "x": 0, "y": 2, "width": 1, "height": 2, "variant": 2 },
        { "id": 14, "type": 3, "x": 6, "y": 2, "width": 1, "height": 2, "variant": 2 },
        { "id": 15, "type": 3, "x": 1, "y": 3, "width": 1, "height": 2, "variant": 2 },
        { "id": 16, "type": 3, "x": 2, "y": 3, "width": 1, "height": 1, "variant": 1 },
        { "id": 17, "type": 3, "x": 3, "y": 3, "width": 1, "height": 1, "variant": 1 },
        { "id": 18, "type": 3, "x": 4, "y": 3, "width": 1, "height": 1, "variant": 1 },
        { "id": 19, "type": 3, "x": 5, "y": 3, "width": 1, "height": 2, "variant": 2 },
        { "id": 20, "type": 3, "x": 0, "y": 4, "width": 1, "height": 2, "variant": 2 },
        { "id": 21, "type": 3, "x": 2, "y": 4, "width": 1, "height": 2, "variant": 2 },
        { "id": 22, "type": 3, "x": 3, "y": 4, "width": 1, "height": 1, "variant": 2 },
        { "id": 23, "type": 3, "x": 4, "y": 4, "width": 1, "height": 2, "variant": 2 },
        { "id": 24, "type": 3, "x": 6, "y": 4, "width": 1, "height": 2, "variant": 2 },
        { "id": 25, "type": 3, "x": 1, "y": 5, "width": 1, "height": 2, "variant": 2 },
        { "id": 26, "type": 3, "x": 3, "y": 5, "width": 1, "height": 2, "variant": 2 },
        { "id": 27, "type": 3, "x": 5, "y": 5, "width": 1, "height": 2, "variant": 2 },
        { "id": 28, "type": 3, "x": 0, "y": 6, "width": 1, "height": 1, "variant": 2 },
        { "id": 29, "type": 3, "x": 2, "y": 6, "width": 1, "height": 1, "variant": 2 },
        { "id": 30, "type": 3, "x": 4, "y": 6, "width": 1, "height": 1, "variant": 2 },
        { "id": 31, "type": 3, "x": 6, "y": 6, "width": 1, "height": 1, "variant": 2 }
      ]
    }
  ]
},
{
  "level": 22,
  difficulty: PlayDialogDifficulty.Common,
  "layers": [
    {
      "grid": [
        [1, 2, 3, 4, 5, 0, 6, 7, 8],
        [9, 10, 11, 12, 13, 0, 14, 15, 16],
        [17, 18, 19, 20, 21, 0, 22, 23, 24],
        [25, 26, 27, 28, 29, 0, 30, 31, 32],
        [33, 33, 33, 34, 34, 34, 35, 35, 35],
        [36, 0, 37, 38, 0, 39, 40, 0, 41],
        [42, 42, 42, 43, 43, 43, 44, 44, 44],
        [45, 0, 46, 47, 0, 48, 49, 0, 50]
      ],
      "pieces": [
        { "id": 1, "type": 4, "x": 0, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 2, "type": 4, "x": 1, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 3, "type": 4, "x": 2, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 4, "type": 4, "x": 3, "y": 0, "width": 1, "height": 1, "variant": 1, "offsetX": 0.5 },
        { "id": 5, "type": 4, "x": 4, "y": 0, "width": 1, "height": 1, "variant": 1, "offsetX": 0.5 },
        { "id": 6, "type": 4, "x": 6, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 7, "type": 4, "x": 7, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 8, "type": 4, "x": 8, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 9, "type": 2, "x": 0, "y": 1, "width": 1, "height": 1 },
        { "id": 10, "type": 2, "x": 1, "y": 1, "width": 1, "height": 1 },
        { "id": 11, "type": 2, "x": 2, "y": 1, "width": 1, "height": 1 },
        { "id": 12, "type": 1, "x": 3, "y": 1, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 13, "type": 1, "x": 4, "y": 1, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 14, "type": 1, "x": 6, "y": 1, "width": 1, "height": 1, "variant": 1 },
        { "id": 15, "type": 1, "x": 7, "y": 1, "width": 1, "height": 1, "variant": 1 },
        { "id": 16, "type": 1, "x": 8, "y": 1, "width": 1, "height": 1, "variant": 1 },
        { "id": 17, "type": 2, "x": 0, "y": 2, "width": 1, "height": 1 },
        { "id": 18, "type": 2, "x": 1, "y": 2, "width": 1, "height": 1 },
        { "id": 19, "type": 2, "x": 2, "y": 2, "width": 1, "height": 1 },
        { "id": 20, "type": 1, "x": 3, "y": 2, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 21, "type": 1, "x": 4, "y": 2, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 22, "type": 1, "x": 6, "y": 2, "width": 1, "height": 1, "variant": 1 },
        { "id": 23, "type": 1, "x": 7, "y": 2, "width": 1, "height": 1, "variant": 1 },
        { "id": 24, "type": 1, "x": 8, "y": 2, "width": 1, "height": 1, "variant": 1 },
        { "id": 25, "type": 2, "x": 0, "y": 3, "width": 1, "height": 1 },
        { "id": 26, "type": 2, "x": 1, "y": 3, "width": 1, "height": 1 },
        { "id": 27, "type": 2, "x": 2, "y": 3, "width": 1, "height": 1 },
        { "id": 28, "type": 1, "x": 3, "y": 3, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 29, "type": 1, "x": 4, "y": 3, "width": 1, "height": 1, "variant": 0, "offsetX": 0.5 },
        { "id": 30, "type": 1, "x": 6, "y": 3, "width": 1, "height": 1, "variant": 1 },
        { "id": 31, "type": 1, "x": 7, "y": 3, "width": 1, "height": 1, "variant": 1 },
        { "id": 32, "type": 1, "x": 8, "y": 3, "width": 1, "height": 1, "variant": 1 },
        { "id": 33, "type": 2, "x": 0, "y": 4, "width": 3, "height": 1 },
        { "id": 34, "type": 1, "x": 3, "y": 4, "width": 3, "height": 1, "variant": 0 },
        { "id": 35, "type": 1, "x": 6, "y": 4, "width": 3, "height": 1, "variant": 1 },
        { "id": 36, "type": 2, "x": 0, "y": 5, "width": 1, "height": 1 },
        { "id": 37, "type": 2, "x": 2, "y": 5, "width": 1, "height": 1 },
        { "id": 38, "type": 1, "x": 3, "y": 5, "width": 1, "height": 1, "variant": 0 },
        { "id": 39, "type": 1, "x": 5, "y": 5, "width": 1, "height": 1, "variant": 0 },
        { "id": 40, "type": 1, "x": 6, "y": 5, "width": 1, "height": 1, "variant": 1 },
        { "id": 41, "type": 1, "x": 8, "y": 5, "width": 1, "height": 1, "variant": 1 },
        { "id": 42, "type": 2, "x": 0, "y": 6, "width": 3, "height": 1 },
        { "id": 43, "type": 1, "x": 3, "y": 6, "width": 3, "height": 1, "variant": 0 },
        { "id": 44, "type": 1, "x": 6, "y": 6, "width": 3, "height": 1, "variant": 1 },
        { "id": 45, "type": 2, "x": 0, "y": 7, "width": 1, "height": 1 },
        { "id": 46, "type": 2, "x": 2, "y": 7, "width": 1, "height": 1 },
        { "id": 47, "type": 1, "x": 3, "y": 7, "width": 1, "height": 1, "variant": 0 },
        { "id": 48, "type": 1, "x": 5, "y": 7, "width": 1, "height": 1, "variant": 0 },
        { "id": 49, "type": 1, "x": 6, "y": 7, "width": 1, "height": 1, "variant": 1 },
        { "id": 50, "type": 1, "x": 8, "y": 7, "width": 1, "height": 1, "variant": 1 }
      ]
    }
  ]
},
{
  "level": 23,
  difficulty: PlayDialogDifficulty.Common,
  "layers": [
    {
      "grid": [
        [1, 2, 3, 4, 5, 6, 7],
        [8, 9, 10, 11, 12, 13, 14],
        [15, 16, 17, 18, 19, 20, 21],
        [22, 23, 24, 25, 26, 27, 28],
        [29, 30, 31, 32, 33, 34, 35],
        [36, 37, 38, 39, 40, 41, 42],
        [43, 44, 45, 46, 47, 48, 49]
      ],
      "pieces": [
        { "id": 1, "type": 2, "x": 0, "y": 0, "width": 1, "height": 1 },
        { "id": 2, "type": 1, "x": 1, "y": 0, "width": 1, "height": 1, "variant": 0 },
        { "id": 3, "type": 2, "x": 2, "y": 0, "width": 1, "height": 1 },
        { "id": 4, "type": 1, "x": 3, "y": 0, "width": 1, "height": 1, "variant": 0 },
        { "id": 5, "type": 2, "x": 4, "y": 0, "width": 1, "height": 1 },
        { "id": 6, "type": 1, "x": 5, "y": 0, "width": 1, "height": 1, "variant": 0 },
        { "id": 7, "type": 2, "x": 6, "y": 0, "width": 1, "height": 1 },
        { "id": 8, "type": 1, "x": 0, "y": 1, "width": 1, "height": 1, "variant": 0 },
        { "id": 9, "type": 2, "x": 1, "y": 1, "width": 1, "height": 1 },
        { "id": 10, "type": 1, "x": 2, "y": 1, "width": 1, "height": 1, "variant": 0 },
        { "id": 11, "type": 2, "x": 3, "y": 1, "width": 1, "height": 1 },
        { "id": 12, "type": 1, "x": 4, "y": 1, "width": 1, "height": 1, "variant": 0 },
        { "id": 13, "type": 2, "x": 5, "y": 1, "width": 1, "height": 1 },
        { "id": 14, "type": 1, "x": 6, "y": 1, "width": 1, "height": 1, "variant": 0 },
        { "id": 15, "type": 2, "x": 0, "y": 2, "width": 1, "height": 1 },
        { "id": 16, "type": 1, "x": 1, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 17, "type": 2, "x": 2, "y": 2, "width": 1, "height": 1 },
        { "id": 18, "type": 1, "x": 3, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 19, "type": 2, "x": 4, "y": 2, "width": 1, "height": 1 },
        { "id": 20, "type": 1, "x": 5, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 21, "type": 2, "x": 6, "y": 2, "width": 1, "height": 1 },
        { "id": 22, "type": 1, "x": 0, "y": 3, "width": 1, "height": 1, "variant": 0 },
        { "id": 23, "type": 2, "x": 1, "y": 3, "width": 1, "height": 1 },
        { "id": 24, "type": 1, "x": 2, "y": 3, "width": 1, "height": 1, "variant": 0 },
        { "id": 25, "type": 2, "x": 3, "y": 3, "width": 1, "height": 1 },
        { "id": 26, "type": 1, "x": 4, "y": 3, "width": 1, "height": 1, "variant": 0 },
        { "id": 27, "type": 2, "x": 5, "y": 3, "width": 1, "height": 1 },
        { "id": 28, "type": 1, "x": 6, "y": 3, "width": 1, "height": 1, "variant": 0 },
        { "id": 29, "type": 2, "x": 0, "y": 4, "width": 1, "height": 1 },
        { "id": 30, "type": 1, "x": 1, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 31, "type": 2, "x": 2, "y": 4, "width": 1, "height": 1 },
        { "id": 32, "type": 1, "x": 3, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 33, "type": 2, "x": 4, "y": 4, "width": 1, "height": 1 },
        { "id": 34, "type": 1, "x": 5, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 35, "type": 2, "x": 6, "y": 4, "width": 1, "height": 1 },
        { "id": 36, "type": 1, "x": 0, "y": 5, "width": 1, "height": 1, "variant": 0 },
        { "id": 37, "type": 2, "x": 1, "y": 5, "width": 1, "height": 1 },
        { "id": 38, "type": 1, "x": 2, "y": 5, "width": 1, "height": 1, "variant": 0 },
        { "id": 39, "type": 2, "x": 3, "y": 5, "width": 1, "height": 1 },
        { "id": 40, "type": 1, "x": 4, "y": 5, "width": 1, "height": 1, "variant": 0 },
        { "id": 41, "type": 2, "x": 5, "y": 5, "width": 1, "height": 1 },
        { "id": 42, "type": 1, "x": 6, "y": 5, "width": 1, "height": 1, "variant": 0 },
        { "id": 43, "type": 2, "x": 0, "y": 6, "width": 1, "height": 1 },
        { "id": 44, "type": 1, "x": 1, "y": 6, "width": 1, "height": 1, "variant": 0 },
        { "id": 45, "type": 2, "x": 2, "y": 6, "width": 1, "height": 1 },
        { "id": 46, "type": 1, "x": 3, "y": 6, "width": 1, "height": 1, "variant": 0 },
        { "id": 47, "type": 2, "x": 4, "y": 6, "width": 1, "height": 1 },
        { "id": 48, "type": 1, "x": 5, "y": 6, "width": 1, "height": 1, "variant": 0 },
        { "id": 49, "type": 2, "x": 6, "y": 6, "width": 1, "height": 1 }
      ]
    }
  ]
},
{
  "level": 24,
  difficulty: PlayDialogDifficulty.Common,
  "layers": [
    {
      "grid": [
        [1, 2, 3, 4, 5, 6, 7, 8, 9],
        [10, 11, 12, 13, 14, 15, 16, 17, 18],
        [19, 20, 21, 22, 23, 24, 25, 26, 27],
        [28, 29, 30, 31, 32, 33, 34, 35, 36],
        [37, 38, 39, 40, 41, 42, 43, 44, 45],
        [46, 47, 48, 49, 50, 51, 52, 53, 54],
        [55, 56, 57, 58, 59, 60, 61, 62, 63],
        [64, 65, 66, 67, 68, 69, 70, 71, 72],
        [73, 74, 75, 76, 77, 78, 79, 80, 81]
      ],
      "pieces": [
        { "id": 1, "type": 1, "x": 0, "y": 0, "width": 1, "height": 1, "variant": 0 },
        { "id": 2, "type": 1, "x": 1, "y": 0, "width": 1, "height": 1, "variant": 0 },
        { "id": 3, "type": 1, "x": 2, "y": 0, "width": 1, "height": 1, "variant": 0 },
        { "id": 4, "type": 1, "x": 3, "y": 0, "width": 1, "height": 1, "variant": 0 },
        { "id": 5, "type": 1, "x": 4, "y": 0, "width": 1, "height": 1, "variant": 0 },
        { "id": 6, "type": 1, "x": 5, "y": 0, "width": 1, "height": 1, "variant": 0 },
        { "id": 7, "type": 1, "x": 6, "y": 0, "width": 1, "height": 1, "variant": 0 },
        { "id": 8, "type": 1, "x": 7, "y": 0, "width": 1, "height": 1, "variant": 0 },
        { "id": 9, "type": 1, "x": 8, "y": 0, "width": 1, "height": 1, "variant": 0 },
        { "id": 10, "type": 2, "x": 0, "y": 1, "width": 1, "height": 1 },
        { "id": 11, "type": 2, "x": 1, "y": 1, "width": 1, "height": 1 },
        { "id": 12, "type": 2, "x": 2, "y": 1, "width": 1, "height": 1 },
        { "id": 13, "type": 2, "x": 3, "y": 1, "width": 1, "height": 1 },
        { "id": 14, "type": 2, "x": 4, "y": 1, "width": 1, "height": 1 },
        { "id": 15, "type": 2, "x": 5, "y": 1, "width": 1, "height": 1 },
        { "id": 16, "type": 2, "x": 6, "y": 1, "width": 1, "height": 1 },
        { "id": 17, "type": 2, "x": 7, "y": 1, "width": 1, "height": 1 },
        { "id": 18, "type": 2, "x": 8, "y": 1, "width": 1, "height": 1 },
        { "id": 19, "type": 1, "x": 0, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 20, "type": 1, "x": 1, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 21, "type": 1, "x": 2, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 22, "type": 1, "x": 3, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 23, "type": 1, "x": 4, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 24, "type": 1, "x": 5, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 25, "type": 1, "x": 6, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 26, "type": 1, "x": 7, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 27, "type": 1, "x": 8, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 28, "type": 2, "x": 0, "y": 3, "width": 1, "height": 1 },
        { "id": 29, "type": 2, "x": 1, "y": 3, "width": 1, "height": 1 },
        { "id": 30, "type": 2, "x": 2, "y": 3, "width": 1, "height": 1 },
        { "id": 31, "type": 2, "x": 3, "y": 3, "width": 1, "height": 1 },
        { "id": 32, "type": 2, "x": 4, "y": 3, "width": 1, "height": 1 },
        { "id": 33, "type": 2, "x": 5, "y": 3, "width": 1, "height": 1 },
        { "id": 34, "type": 2, "x": 6, "y": 3, "width": 1, "height": 1 },
        { "id": 35, "type": 2, "x": 7, "y": 3, "width": 1, "height": 1 },
        { "id": 36, "type": 2, "x": 8, "y": 3, "width": 1, "height": 1 },
        { "id": 37, "type": 1, "x": 0, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 38, "type": 1, "x": 1, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 39, "type": 1, "x": 2, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 40, "type": 1, "x": 3, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 41, "type": 1, "x": 4, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 42, "type": 1, "x": 5, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 43, "type": 1, "x": 6, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 44, "type": 1, "x": 7, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 45, "type": 1, "x": 8, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 46, "type": 2, "x": 0, "y": 5, "width": 1, "height": 1 },
        { "id": 47, "type": 2, "x": 1, "y": 5, "width": 1, "height": 1 },
        { "id": 48, "type": 2, "x": 2, "y": 5, "width": 1, "height": 1 },
        { "id": 49, "type": 2, "x": 3, "y": 5, "width": 1, "height": 1 },
        { "id": 50, "type": 2, "x": 4, "y": 5, "width": 1, "height": 1 },
        { "id": 51, "type": 2, "x": 5, "y": 5, "width": 1, "height": 1 },
        { "id": 52, "type": 2, "x": 6, "y": 5, "width": 1, "height": 1 },
        { "id": 53, "type": 2, "x": 7, "y": 5, "width": 1, "height": 1 },
        { "id": 54, "type": 2, "x": 8, "y": 5, "width": 1, "height": 1 },
        { "id": 55, "type": 1, "x": 0, "y": 6, "width": 1, "height": 1, "variant": 0 },
        { "id": 56, "type": 1, "x": 1, "y": 6, "width": 1, "height": 1, "variant": 0 },
        { "id": 57, "type": 1, "x": 2, "y": 6, "width": 1, "height": 1, "variant": 0 },
        { "id": 58, "type": 1, "x": 3, "y": 6, "width": 1, "height": 1, "variant": 0 },
        { "id": 59, "type": 1, "x": 4, "y": 6, "width": 1, "height": 1, "variant": 0 },
        { "id": 60, "type": 1, "x": 5, "y": 6, "width": 1, "height": 1, "variant": 0 },
        { "id": 61, "type": 1, "x": 6, "y": 6, "width": 1, "height": 1, "variant": 0 },
        { "id": 62, "type": 1, "x": 7, "y": 6, "width": 1, "height": 1, "variant": 0 },
        { "id": 63, "type": 1, "x": 8, "y": 6, "width": 1, "height": 1, "variant": 0 },
        { "id": 64, "type": 2, "x": 0, "y": 7, "width": 1, "height": 1 },
        { "id": 65, "type": 2, "x": 1, "y": 7, "width": 1, "height": 1 },
        { "id": 66, "type": 2, "x": 2, "y": 7, "width": 1, "height": 1 },
        { "id": 67, "type": 2, "x": 3, "y": 7, "width": 1, "height": 1 },
        { "id": 68, "type": 2, "x": 4, "y": 7, "width": 1, "height": 1 },
        { "id": 69, "type": 2, "x": 5, "y": 7, "width": 1, "height": 1 },
        { "id": 70, "type": 2, "x": 6, "y": 7, "width": 1, "height": 1 },
        { "id": 71, "type": 2, "x": 7, "y": 7, "width": 1, "height": 1 },
        { "id": 72, "type": 2, "x": 8, "y": 7, "width": 1, "height": 1 },
        { "id": 73, "type": 1, "x": 0, "y": 8, "width": 1, "height": 1, "variant": 0 },
        { "id": 74, "type": 1, "x": 1, "y": 8, "width": 1, "height": 1, "variant": 0 },
        { "id": 75, "type": 1, "x": 2, "y": 8, "width": 1, "height": 1, "variant": 0 },
        { "id": 76, "type": 1, "x": 3, "y": 8, "width": 1, "height": 1, "variant": 0 },
        { "id": 77, "type": 1, "x": 4, "y": 8, "width": 1, "height": 1, "variant": 0 },
        { "id": 78, "type": 1, "x": 5, "y": 8, "width": 1, "height": 1, "variant": 0 },
        { "id": 79, "type": 1, "x": 6, "y": 8, "width": 1, "height": 1, "variant": 0 },
        { "id": 80, "type": 1, "x": 7, "y": 8, "width": 1, "height": 1, "variant": 0 },
        { "id": 81, "type": 1, "x": 8, "y": 8, "width": 1, "height": 1, "variant": 0 }
      ]
    }
  ]
},
{
  "level": 25,
  "difficulty": PlayDialogDifficulty.Common,
  "layers": [
    {
      "grid": [
        [1, 2, 3, 4, 5, 6],
        [7, 8, 9, 10, 11, 12],
        [13, 14, 15, 16, 17, 18],
        [19, 20, 21, 22, 23, 24],
        [25, 26, 27, 28, 29, 30],
        [31, 32, 33, 34, 35, 36]
      ],
      "pieces": [
        { "id": 1, "type": 1, "x": 0, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 2, "type": 3, "x": 1, "y": 0, "width": 1, "height": 1, "variant": 0 },
        { "id": 3, "type": 1, "x": 2, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 4, "type": 3, "x": 3, "y": 0, "width": 1, "height": 1, "variant": 0 },
        { "id": 5, "type": 1, "x": 4, "y": 0, "width": 1, "height": 1, "variant": 1 },
        { "id": 6, "type": 3, "x": 5, "y": 0, "width": 1, "height": 1, "variant": 0 },
        { "id": 7, "type": 1, "x": 0, "y": 1, "width": 1, "height": 1, "variant": 1 },
        { "id": 8, "type": 3, "x": 1, "y": 1, "width": 1, "height": 1, "variant": 0 },
        { "id": 9, "type": 1, "x": 2, "y": 1, "width": 1, "height": 1, "variant": 1 },
        { "id": 10, "type": 3, "x": 3, "y": 1, "width": 1, "height": 1, "variant": 0 },
        { "id": 11, "type": 1, "x": 4, "y": 1, "width": 1, "height": 1, "variant": 1 },
        { "id": 12, "type": 3, "x": 5, "y": 1, "width": 1, "height": 1, "variant": 0 },
        { "id": 13, "type": 1, "x": 0, "y": 2, "width": 1, "height": 1, "variant": 1 },
        { "id": 14, "type": 3, "x": 1, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 15, "type": 1, "x": 2, "y": 2, "width": 1, "height": 1, "variant": 1 },
        { "id": 16, "type": 3, "x": 3, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 17, "type": 1, "x": 4, "y": 2, "width": 1, "height": 1, "variant": 1 },
        { "id": 18, "type": 3, "x": 5, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 19, "type": 3, "x": 0, "y": 3, "width": 1, "height": 1, "variant": 0 },
        { "id": 20, "type": 1, "x": 1, "y": 3, "width": 1, "height": 1, "variant": 1 },
        { "id": 21, "type": 3, "x": 2, "y": 3, "width": 1, "height": 1, "variant": 0 },
        { "id": 22, "type": 1, "x": 3, "y": 3, "width": 1, "height": 1, "variant": 1 },
        { "id": 23, "type": 3, "x": 4, "y": 3, "width": 1, "height": 1, "variant": 0 },
        { "id": 24, "type": 1, "x": 5, "y": 3, "width": 1, "height": 1, "variant": 1 },
        { "id": 25, "type": 3, "x": 0, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 26, "type": 1, "x": 1, "y": 4, "width": 1, "height": 1, "variant": 1 },
        { "id": 27, "type": 3, "x": 2, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 28, "type": 1, "x": 3, "y": 4, "width": 1, "height": 1, "variant": 1 },
        { "id": 29, "type": 3, "x": 4, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 30, "type": 1, "x": 5, "y": 4, "width": 1, "height": 1, "variant": 1 },
        { "id": 31, "type": 3, "x": 0, "y": 5, "width": 1, "height": 1, "variant": 0 },
        { "id": 32, "type": 1, "x": 1, "y": 5, "width": 1, "height": 1, "variant": 1 },
        { "id": 33, "type": 3, "x": 2, "y": 5, "width": 1, "height": 1, "variant": 0 },
        { "id": 34, "type": 1, "x": 3, "y": 5, "width": 1, "height": 1, "variant": 1 },
        { "id": 35, "type": 3, "x": 4, "y": 5, "width": 1, "height": 1, "variant": 0 },
        { "id": 36, "type": 1, "x": 5, "y": 5, "width": 1, "height": 1, "variant": 1 }
      ]
    }
  ]
},
  {
    level: 25,
    difficulty: PlayDialogDifficulty.Common,
    layers: [
      {
        grid: [
          [0, 0, 0, 0, 0, 0, 0, 0],
          [0, 0, 0, 0, 0, 0, 0, 0],
          [0, 0, 0, 0, 0, 0, 0, 0],
          [0, 0, 0, 0, 0, 0, 0, 0],
          [1, 2, 0, 0, 0, 0, 3, 4],
          [5, 6, 0, 0, 0, 0, 7, 8],
          [9, 10, 0, 0, 0, 0, 11, 12]
        ],
        pieces: [
          { id: 1, type: LevelPieceType.Cube, x: 0, y: 4, width: 1, height: 1, variant: 1 },
          { id: 2, type: LevelPieceType.Cube, x: 1, y: 4, width: 1, height: 1, variant: 1 },
          { id: 3, type: LevelPieceType.Cube, x: 6, y: 4, width: 1, height: 1, variant: 1 },
          { id: 4, type: LevelPieceType.Cube, x: 7, y: 4, width: 1, height: 1, variant: 1 },
          { id: 5, type: LevelPieceType.Cube, x: 0, y: 5, width: 1, height: 1, variant: 1 },
          { id: 6, type: LevelPieceType.Cube, x: 1, y: 5, width: 1, height: 1, variant: 1 },
          { id: 7, type: LevelPieceType.Cube, x: 6, y: 5, width: 1, height: 1, variant: 1 },
          { id: 8, type: LevelPieceType.Cube, x: 7, y: 5, width: 1, height: 1, variant: 1 },
          { id: 9, type: LevelPieceType.Cube, x: 0, y: 6, width: 1, height: 1, variant: 1 },
          { id: 10, type: LevelPieceType.Cube, x: 1, y: 6, width: 1, height: 1, variant: 1 },
          { id: 11, type: LevelPieceType.Cube, x: 6, y: 6, width: 1, height: 1, variant: 1 },
          { id: 12, type: LevelPieceType.Cube, x: 7, y: 6, width: 1, height: 1, variant: 1 },
        ],
      },
      {
        grid: [
          [1, 2, 3, 0, 0, 4, 5, 6],
          [1, 2, 3, 7, 0, 4, 5, 6],
          [8, 8, 8, 9, 0, 10, 10, 10],
          [11, 11, 12, 12, 12, 0, 13, 13],
          [14, 15, 16, 0, 17, 0, 18, 19],
          [20, 21, 22, 0, 23, 0, 24, 25],
          [26, 27, 28, 0, 29, 0, 30, 31]
        ],
        pieces: [
          { id: 1, type: LevelPieceType.Cube, x: 0, y: 0, width: 1, height: 2, variant: 0 },
          { id: 2, type: LevelPieceType.Cube, x: 1, y: 0, width: 1, height: 2, variant: 0 },
          { id: 3, type: LevelPieceType.Cube, x: 2, y: 0, width: 1, height: 2, variant: 0 },
          { id: 4, type: LevelPieceType.Cube, x: 5, y: 0, width: 1, height: 2, variant: 0 },
          { id: 5, type: LevelPieceType.Cube, x: 6, y: 0, width: 1, height: 2, variant: 0 },
          { id: 6, type: LevelPieceType.Cube, x: 7, y: 0, width: 1, height: 2, variant: 0 },
          { id: 7, type: LevelPieceType.Cone, x: 3, y: 1, width: 1, height: 1, variant: 1, offsetX: 0.5 },
          { id: 8, type: LevelPieceType.Cube, x: 0, y: 2, width: 3, height: 1, variant: 1 },
          { id: 9, type: LevelPieceType.Cube, x: 3, y: 2, width: 1, height: 1, variant: 0, offsetX: 0.5 },
          { id: 10, type: LevelPieceType.Cube, x: 5, y: 2, width: 3, height: 1, variant: 1 },
          { id: 11, type: LevelPieceType.Cube, x: 0, y: 3, width: 2, height: 1, variant: 1 },
          { id: 12, type: LevelPieceType.Cube, x: 2, y: 3, width: 3, height: 1, variant: 1, offsetX: 0.5 },
          { id: 13, type: LevelPieceType.Cube, x: 6, y: 3, width: 2, height: 1, variant: 1 },
          { id: 14, type: LevelPieceType.Jar, x: 0, y: 4, width: 1, height: 1, variant: 2 },
          { id: 15, type: LevelPieceType.Jar, x: 1, y: 4, width: 1, height: 1, variant: 2 },
          { id: 16, type: LevelPieceType.Jar, x: 2, y: 4, width: 1, height: 1, variant: 2, offsetX: 0.5 },
          { id: 17, type: LevelPieceType.Jar, x: 4, y: 4, width: 1, height: 1, variant: 2, offsetX: 0.5 },
          { id: 18, type: LevelPieceType.Jar, x: 6, y: 4, width: 1, height: 1, variant: 2 },
          { id: 19, type: LevelPieceType.Jar, x: 7, y: 4, width: 1, height: 1, variant: 2 },
          { id: 20, type: LevelPieceType.Jar, x: 0, y: 5, width: 1, height: 1, variant: 2 },
          { id: 21, type: LevelPieceType.Jar, x: 1, y: 5, width: 1, height: 1, variant: 2 },
          { id: 22, type: LevelPieceType.Jar, x: 2, y: 5, width: 1, height: 1, variant: 2, offsetX: 0.5 },
          { id: 23, type: LevelPieceType.Jar, x: 4, y: 5, width: 1, height: 1, variant: 2, offsetX: 0.5 },
          { id: 24, type: LevelPieceType.Jar, x: 6, y: 5, width: 1, height: 1, variant: 2 },
          { id: 25, type: LevelPieceType.Jar, x: 7, y: 5, width: 1, height: 1, variant: 2 },
          { id: 26, type: LevelPieceType.Jar, x: 0, y: 6, width: 1, height: 1, variant: 2 },
          { id: 27, type: LevelPieceType.Jar, x: 1, y: 6, width: 1, height: 1, variant: 2 },
          { id: 28, type: LevelPieceType.Jar, x: 2, y: 6, width: 1, height: 1, variant: 2, offsetX: 0.5 },
          { id: 29, type: LevelPieceType.Jar, x: 4, y: 6, width: 1, height: 1, variant: 2, offsetX: 0.5 },
          { id: 30, type: LevelPieceType.Jar, x: 6, y: 6, width: 1, height: 1, variant: 2 },
          { id: 31, type: LevelPieceType.Jar, x: 7, y: 6, width: 1, height: 1, variant: 2 },
        ],
      },
    ],
  },
{
  "level": 27,
  "difficulty": PlayDialogDifficulty.Common,
  "layers": [
    {
      "grid": [
        [1, 2, 3, 4, 5, 6, 7, 8, 9],
        [10, 11, 12, 13, 14, 15, 16, 17, 18],
        [19, 20, 21, 22, 23, 24, 25, 26, 27],
        [28, 29, 30, 31, 32, 33, 34, 35, 36],
        [37, 38, 39, 40, 41, 42, 43, 44, 45],
        [46, 47, 48, 49, 50, 51, 52, 53, 54],
        [55, 56, 57, 58, 59, 60, 61, 62, 63],
        [64, 65, 66, 67, 68, 69, 70, 71, 72],
        [73, 74, 75, 76, 77, 78, 79, 80, 81]
      ],
      "pieces": [
        { "id": 1, "type": 3, "x": 0, "y": 0, "width": 1, "height": 1, "variant": 0 },
        { "id": 2, "type": 2, "x": 1, "y": 0, "width": 1, "height": 1 },
        { "id": 3, "type": 2, "x": 2, "y": 0, "width": 1, "height": 1 },
        { "id": 4, "type": 3, "x": 3, "y": 0, "width": 1, "height": 1, "variant": 0 },
        { "id": 5, "type": 3, "x": 4, "y": 0, "width": 1, "height": 1, "variant": 0 },
        { "id": 6, "type": 2, "x": 5, "y": 0, "width": 1, "height": 1 },
        { "id": 7, "type": 2, "x": 6, "y": 0, "width": 1, "height": 1 },
        { "id": 8, "type": 3, "x": 7, "y": 0, "width": 1, "height": 1, "variant": 0 },
        { "id": 9, "type": 3, "x": 8, "y": 0, "width": 1, "height": 1, "variant": 0 },
        { "id": 10, "type": 2, "x": 0, "y": 1, "width": 1, "height": 1 },
        { "id": 11, "type": 2, "x": 1, "y": 1, "width": 1, "height": 1 },
        { "id": 12, "type": 3, "x": 2, "y": 1, "width": 1, "height": 1, "variant": 0 },
        { "id": 13, "type": 3, "x": 3, "y": 1, "width": 1, "height": 1, "variant": 0 },
        { "id": 14, "type": 2, "x": 4, "y": 1, "width": 1, "height": 1 },
        { "id": 15, "type": 2, "x": 5, "y": 1, "width": 1, "height": 1 },
        { "id": 16, "type": 3, "x": 6, "y": 1, "width": 1, "height": 1, "variant": 0 },
        { "id": 17, "type": 3, "x": 7, "y": 1, "width": 1, "height": 1, "variant": 0 },
        { "id": 18, "type": 2, "x": 8, "y": 1, "width": 1, "height": 1 },
        { "id": 19, "type": 2, "x": 0, "y": 2, "width": 1, "height": 1 },
        { "id": 20, "type": 3, "x": 1, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 21, "type": 3, "x": 2, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 22, "type": 2, "x": 3, "y": 2, "width": 1, "height": 1 },
        { "id": 23, "type": 2, "x": 4, "y": 2, "width": 1, "height": 1 },
        { "id": 24, "type": 3, "x": 5, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 25, "type": 3, "x": 6, "y": 2, "width": 1, "height": 1, "variant": 0 },
        { "id": 26, "type": 2, "x": 7, "y": 2, "width": 1, "height": 1 },
        { "id": 27, "type": 2, "x": 8, "y": 2, "width": 1, "height": 1 },
        { "id": 28, "type": 3, "x": 0, "y": 3, "width": 1, "height": 1, "variant": 0 },
        { "id": 29, "type": 3, "x": 1, "y": 3, "width": 1, "height": 1, "variant": 0 },
        { "id": 30, "type": 2, "x": 2, "y": 3, "width": 1, "height": 1 },
        { "id": 31, "type": 2, "x": 3, "y": 3, "width": 1, "height": 1 },
        { "id": 32, "type": 3, "x": 4, "y": 3, "width": 1, "height": 1, "variant": 0 },
        { "id": 33, "type": 3, "x": 5, "y": 3, "width": 1, "height": 1 },
        { "id": 34, "type": 2, "x": 6, "y": 3, "width": 1, "height": 1 },
        { "id": 35, "type": 2, "x": 7, "y": 3, "width": 1, "height": 1 },
        { "id": 36, "type": 3, "x": 8, "y": 3, "width": 1, "height": 1, "variant": 0 },
        { "id": 37, "type": 3, "x": 0, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 38, "type": 2, "x": 1, "y": 4, "width": 1, "height": 1 },
        { "id": 39, "type": 2, "x": 2, "y": 4, "width": 1, "height": 1 },
        { "id": 40, "type": 3, "x": 3, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 41, "type": 3, "x": 4, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 42, "type": 2, "x": 5, "y": 4, "width": 1, "height": 1 },
        { "id": 43, "type": 2, "x": 6, "y": 4, "width": 1, "height": 1 },
        { "id": 44, "type": 3, "x": 7, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 45, "type": 3, "x": 8, "y": 4, "width": 1, "height": 1, "variant": 0 },
        { "id": 46, "type": 2, "x": 0, "y": 5, "width": 1, "height": 1 },
        { "id": 47, "type": 2, "x": 1, "y": 5, "width": 1, "height": 1 },
        { "id": 48, "type": 3, "x": 2, "y": 5, "width": 1, "height": 1, "variant": 0 },
        { "id": 49, "type": 3, "x": 3, "y": 5, "width": 1, "height": 1, "variant": 0 },
        { "id": 50, "type": 2, "x": 4, "y": 5, "width": 1, "height": 1 },
        { "id": 51, "type": 2, "x": 5, "y": 5, "width": 1, "height": 1 },
        { "id": 52, "type": 3, "x": 6, "y": 5, "width": 1, "height": 1, "variant": 0 },
        { "id": 53, "type": 3, "x": 7, "y": 5, "width": 1, "height": 1, "variant": 0 },
        { "id": 54, "type": 2, "x": 8, "y": 5, "width": 1, "height": 1 },
        { "id": 55, "type": 2, "x": 0, "y": 6, "width": 1, "height": 1 },
        { "id": 56, "type": 3, "x": 1, "y": 6, "width": 1, "height": 1, "variant": 0 },
        { "id": 57, "type": 3, "x": 2, "y": 6, "width": 1, "height": 1, "variant": 0 },
        { "id": 58, "type": 2, "x": 3, "y": 6, "width": 1, "height": 1 },
        { "id": 59, "type": 2, "x": 4, "y": 6, "width": 1, "height": 1 },
        { "id": 60, "type": 3, "x": 5, "y": 6, "width": 1, "height": 1, "variant": 0 },
        { "id": 61, "type": 3, "x": 6, "y": 6, "width": 1, "height": 1, "variant": 0 },
        { "id": 62, "type": 2, "x": 7, "y": 6, "width": 1, "height": 1 },
        { "id": 63, "type": 2, "x": 8, "y": 6, "width": 1, "height": 1 },
        { "id": 64, "type": 3, "x": 0, "y": 7, "width": 1, "height": 1, "variant": 0 },
        { "id": 65, "type": 3, "x": 1, "y": 7, "width": 1, "height": 1, "variant": 0 },
        { "id": 66, "type": 2, "x": 2, "y": 7, "width": 1, "height": 1 },
        { "id": 67, "type": 2, "x": 3, "y": 7, "width": 1, "height": 1 },
        { "id": 68, "type": 3, "x": 4, "y": 7, "width": 1, "height": 1, "variant": 0 },
        { "id": 69, "type": 3, "x": 5, "y": 7, "width": 1, "height": 1, "variant": 0 },
        { "id": 70, "type": 2, "x": 6, "y": 7, "width": 1, "height": 1 },
        { "id": 71, "type": 2, "x": 7, "y": 7, "width": 1, "height": 1 },
        { "id": 72, "type": 3, "x": 8, "y": 7, "width": 1, "height": 1, "variant": 0 },
        { "id": 73, "type": 3, "x": 0, "y": 8, "width": 1, "height": 1, "variant": 0 },
        { "id": 74, "type": 2, "x": 1, "y": 8, "width": 1, "height": 1 },
        { "id": 75, "type": 2, "x": 2, "y": 8, "width": 1, "height": 1 },
        { "id": 76, "type": 3, "x": 3, "y": 8, "width": 1, "height": 1, "variant": 0 },
        { "id": 77, "type": 3, "x": 4, "y": 8, "width": 1, "height": 1, "variant": 0 },
        { "id": 78, "type": 2, "x": 5, "y": 8, "width": 1, "height": 1 },
        { "id": 79, "type": 2, "x": 6, "y": 8, "width": 1, "height": 1 },
        { "id": 80, "type": 3, "x": 7, "y": 8, "width": 1, "height": 1, "variant": 0 },
        { "id": 81, "type": 3, "x": 8, "y": 8, "width": 1, "height": 1, "variant": 0 }
      ]
    }
  ]
},
  { level:  27, difficulty: PlayDialogDifficulty.Common },
  { level:  28, difficulty: PlayDialogDifficulty.Common },
  { level:  29, difficulty: PlayDialogDifficulty.Common },
  { level:  30, difficulty: PlayDialogDifficulty.Common },
  { level:  31, difficulty: PlayDialogDifficulty.Common },
  { level:  32, difficulty: PlayDialogDifficulty.Common },
  { level:  33, difficulty: PlayDialogDifficulty.Common },
  { level:  34, difficulty: PlayDialogDifficulty.Common },
  { level:  35, difficulty: PlayDialogDifficulty.Common },
  { level:  36, difficulty: PlayDialogDifficulty.Common },
  { level:  37, difficulty: PlayDialogDifficulty.Common },
  { level:  38, difficulty: PlayDialogDifficulty.Common },
  { level:  39, difficulty: PlayDialogDifficulty.Common },
  { level:  40, difficulty: PlayDialogDifficulty.Common },
  { level:  41, difficulty: PlayDialogDifficulty.Common },
  { level:  42, difficulty: PlayDialogDifficulty.Common },
  { level:  43, difficulty: PlayDialogDifficulty.Common },
  { level:  44, difficulty: PlayDialogDifficulty.Common },
  { level:  45, difficulty: PlayDialogDifficulty.Common },
  { level:  46, difficulty: PlayDialogDifficulty.Common },
  { level:  47, difficulty: PlayDialogDifficulty.Common },
  { level:  48, difficulty: PlayDialogDifficulty.Common },
  { level:  49, difficulty: PlayDialogDifficulty.Common },
  { level:  50, difficulty: PlayDialogDifficulty.Common },
  { level:  51, difficulty: PlayDialogDifficulty.Common },
  { level:  52, difficulty: PlayDialogDifficulty.Common },
  { level:  53, difficulty: PlayDialogDifficulty.Common },
  { level:  54, difficulty: PlayDialogDifficulty.Common },
  { level:  55, difficulty: PlayDialogDifficulty.Common },
  { level:  56, difficulty: PlayDialogDifficulty.Common },
  { level:  57, difficulty: PlayDialogDifficulty.Common },
  { level:  58, difficulty: PlayDialogDifficulty.Common },
  { level:  59, difficulty: PlayDialogDifficulty.Common },
  { level:  60, difficulty: PlayDialogDifficulty.Common },
  { level:  61, difficulty: PlayDialogDifficulty.Common },
  { level:  62, difficulty: PlayDialogDifficulty.Common },
  { level:  63, difficulty: PlayDialogDifficulty.Common },
  { level:  64, difficulty: PlayDialogDifficulty.Common },
  { level:  65, difficulty: PlayDialogDifficulty.Common },
  { level:  66, difficulty: PlayDialogDifficulty.Common },
  { level:  67, difficulty: PlayDialogDifficulty.Common },
  { level:  68, difficulty: PlayDialogDifficulty.Common },
  { level:  69, difficulty: PlayDialogDifficulty.Common },
  { level:  70, difficulty: PlayDialogDifficulty.Common },
  { level:  71, difficulty: PlayDialogDifficulty.Common },
  { level:  72, difficulty: PlayDialogDifficulty.Common },
  { level:  73, difficulty: PlayDialogDifficulty.Common },
  { level:  74, difficulty: PlayDialogDifficulty.Common },
  { level:  75, difficulty: PlayDialogDifficulty.Common },
  { level:  76, difficulty: PlayDialogDifficulty.Common },
  { level:  77, difficulty: PlayDialogDifficulty.Common },
  { level:  78, difficulty: PlayDialogDifficulty.Common },
  { level:  79, difficulty: PlayDialogDifficulty.Common },
  { level:  80, difficulty: PlayDialogDifficulty.Common },
  { level:  81, difficulty: PlayDialogDifficulty.Common },
  { level:  82, difficulty: PlayDialogDifficulty.Common },
  { level:  83, difficulty: PlayDialogDifficulty.Common },
  { level:  84, difficulty: PlayDialogDifficulty.Common },
  { level:  85, difficulty: PlayDialogDifficulty.Common },
  { level:  86, difficulty: PlayDialogDifficulty.Common },
  { level:  87, difficulty: PlayDialogDifficulty.Common },
  { level:  88, difficulty: PlayDialogDifficulty.Common },
  { level:  89, difficulty: PlayDialogDifficulty.Common },
  { level:  90, difficulty: PlayDialogDifficulty.Common },
  { level:  91, difficulty: PlayDialogDifficulty.Common },
  { level:  92, difficulty: PlayDialogDifficulty.Common },
  { level:  93, difficulty: PlayDialogDifficulty.Common },
  { level:  94, difficulty: PlayDialogDifficulty.Common },
  { level:  95, difficulty: PlayDialogDifficulty.Common },
  { level:  96, difficulty: PlayDialogDifficulty.Common },
  { level:  97, difficulty: PlayDialogDifficulty.Common },
  { level:  98, difficulty: PlayDialogDifficulty.Common },
  { level: 99, difficulty: PlayDialogDifficulty.Common },
];

// Bundled authored levels are only an offline fallback. During a normal run,
// the segment-resolved server release takes priority for every campaign level.
export const LEVELS_CONFIG: LevelConfig[] = LEGACY_LEVELS_CONFIG.filter( config => config.level < 1000 );

if ( !GlobalSettings.IS_PROD )
{
  for ( const config of LEVELS_CONFIG )
  {
    validateLayerCount( config );
    config.layers?.forEach( validateLevelLayer );
    validateTables( config );
  }
}

const LEVEL_CONFIG_BY_LEVEL = new Map( LEVELS_CONFIG.map( c => [ c.level, c ] ) );

export function areBundledLevelsAllowed(): boolean
{
  return TestLevelBridge.isLocalLevelsForced()
    || TestLevelBridge.isActive()
    || TestLevelBridge.isBatchActive()
    || StressTest.isActive()
    || BlockShowcase.isActive()
    || JarPyramidShowcase.isActive()
    || DiamondTowerShowcase.isActive()
    || TiltedTableShowcase.isActive()
    || RotatedTableShowcase.isActive()
    || ShelfTableShowcase.isActive()
    || HangingTableShowcase.isActive()
    || ElevatorTableShowcase.isActive()
    || SlideTableShowcase.isActive()
    || TableCaseShowcase.isActive()
    || LeaningLogsShowcase.isActive()
    || JarCasesShowcase.isActive()
    || JarSandwichShowcase.isActive()
    || JarBayShowcase.isActive()
    || TextureTest.isActive()
    || TableFormationShowcase.isActive()
    || BarrierShowcase.isActive();
}

export function getMaxCampaignLevel(): number
{
  const pack_max = LevelPackService.getMaxLevel();
  if ( pack_max > 0 )
    return pack_max;

  if ( !areBundledLevelsAllowed() )
    return 0;

  let bundled_max = 0;
  for ( const config of LEVELS_CONFIG )
  {
    const has_layout =
      ( config.tables?.length ?? 0 ) > 0 ||
      ( config.layers?.length ?? 0 ) > 0;
    if ( has_layout )
      bundled_max = Math.max( bundled_max, config.level );
  }
  return bundled_max;
}

export function hasCampaignLevel( level: number ): boolean
{
  if ( level <= 0 || level > getMaxCampaignLevel() )
    return false;

  if ( areBundledLevelsAllowed() )
    return true;

  return LevelPackService.hasLevel( level );
}

export function getLevelDifficulty( level: number ): PlayDialogDifficulty
{
  const new_block_models_config = NewBlockModelsShowcase.getConfig( level );
  if ( new_block_models_config !== null )
    return new_block_models_config.difficulty;

  const brick_levels_config = BrickLevelsShowcase.getConfig( level );
  if ( brick_levels_config !== null )
    return brick_levels_config.difficulty;

  const sticky_mine_config = StickyMineShowcase.getConfig( level );
  if ( sticky_mine_config !== null )
    return sticky_mine_config.difficulty;

  const barrier_config = BarrierShowcase.getConfig( level );
  if ( barrier_config !== null )
    return barrier_config.difficulty;

  const showcase_config = BlockShowcase.getConfig( level );
  if ( showcase_config !== null )
    return showcase_config.difficulty;

  const jar_pyramid_config = JarPyramidShowcase.getConfig( level );
  if ( jar_pyramid_config !== null )
    return jar_pyramid_config.difficulty;

  const diamond_tower_config = DiamondTowerShowcase.getConfig( level );
  if ( diamond_tower_config !== null )
    return diamond_tower_config.difficulty;

  const tilted_table_config = TiltedTableShowcase.getConfig( level );
  if ( tilted_table_config !== null )
    return tilted_table_config.difficulty;

  const rotated_table_difficulty = RotatedTableShowcase.getConfig( level );
  if ( rotated_table_difficulty !== null )
    return rotated_table_difficulty.difficulty;

  const shelf_table_config = ShelfTableShowcase.getConfig( level );
  if ( shelf_table_config !== null )
    return shelf_table_config.difficulty;

  const hanging_table_config = HangingTableShowcase.getConfig( level );
  if ( hanging_table_config !== null )
    return hanging_table_config.difficulty;

  const elevator_table_config = ElevatorTableShowcase.getConfig( level );
  if ( elevator_table_config !== null )
    return elevator_table_config.difficulty;

  const slide_table_config = SlideTableShowcase.getConfig( level );
  if ( slide_table_config !== null )
    return slide_table_config.difficulty;

  const table_case_config = TableCaseShowcase.getConfig( level );
  if ( table_case_config !== null )
    return table_case_config.difficulty;

  const leaning_logs_config = LeaningLogsShowcase.getConfig( level );
  if ( leaning_logs_config !== null )
    return leaning_logs_config.difficulty;

  const jar_cases_config = JarCasesShowcase.getConfig( level );
  if ( jar_cases_config !== null )
    return jar_cases_config.difficulty;

  const jar_sandwich_config = JarSandwichShowcase.getConfig( level );
  if ( jar_sandwich_config !== null )
    return jar_sandwich_config.difficulty;

  const jar_bay_config = JarBayShowcase.getConfig( level );
  if ( jar_bay_config !== null )
    return jar_bay_config.difficulty;

  const stress_config = StressTest.getConfig( level );
  if ( stress_config !== null )
    return stress_config.difficulty;

  const texture_config = TextureTest.getConfig( level );
  if ( texture_config !== null )
    return texture_config.difficulty;

  const table_formation_config = TableFormationShowcase.getConfig( level );
  if ( table_formation_config !== null )
    return table_formation_config.difficulty;

  const test_config = TestLevelBridge.getConfig( level );
  if ( test_config !== null )
    return test_config.difficulty;

  // A server release carries the tier assigned by the balance curve.
  const pack_difficulty = LevelPackService.getDifficulty( level );
  if ( pack_difficulty !== null )
    return pack_difficulty;

  const bundled_config = LEVEL_CONFIG_BY_LEVEL.get( level );
  if ( bundled_config !== undefined )
    return bundled_config.difficulty;

  return PlayDialogDifficulty.Common;
}

export function getLevelConfig( level: number ): LevelConfig
{
  const new_block_models_config = NewBlockModelsShowcase.getConfig( level );
  if ( new_block_models_config !== null )
    return new_block_models_config;

  const brick_levels_config = BrickLevelsShowcase.getConfig( level );
  if ( brick_levels_config !== null )
    return brick_levels_config;

  const sticky_mine_config = StickyMineShowcase.getConfig( level );
  if ( sticky_mine_config !== null )
    return sticky_mine_config;

  const barrier_config = BarrierShowcase.getConfig( level );
  if ( barrier_config !== null )
    return barrier_config;

  const showcase_config = BlockShowcase.getConfig( level );
  if ( showcase_config !== null )
    return showcase_config;

  const jar_pyramid_config = JarPyramidShowcase.getConfig( level );
  if ( jar_pyramid_config !== null )
    return jar_pyramid_config;

  const diamond_tower_config = DiamondTowerShowcase.getConfig( level );
  if ( diamond_tower_config !== null )
    return diamond_tower_config;

  const tilted_table_config = TiltedTableShowcase.getConfig( level );
  if ( tilted_table_config !== null )
    return tilted_table_config;

  const rotated_table_config = RotatedTableShowcase.getConfig( level );
  if ( rotated_table_config !== null )
    return rotated_table_config;

  const shelf_table_config = ShelfTableShowcase.getConfig( level );
  if ( shelf_table_config !== null )
    return shelf_table_config;

  const hanging_table_config = HangingTableShowcase.getConfig( level );
  if ( hanging_table_config !== null )
    return hanging_table_config;

  const elevator_table_config = ElevatorTableShowcase.getConfig( level );
  if ( elevator_table_config !== null )
    return elevator_table_config;

  const slide_table_config = SlideTableShowcase.getConfig( level );
  if ( slide_table_config !== null )
    return slide_table_config;

  const table_case_config = TableCaseShowcase.getConfig( level );
  if ( table_case_config !== null )
    return table_case_config;

  const leaning_logs_config = LeaningLogsShowcase.getConfig( level );
  if ( leaning_logs_config !== null )
    return leaning_logs_config;

  const jar_cases_config = JarCasesShowcase.getConfig( level );
  if ( jar_cases_config !== null )
    return jar_cases_config;

  const jar_sandwich_config = JarSandwichShowcase.getConfig( level );
  if ( jar_sandwich_config !== null )
    return jar_sandwich_config;

  const jar_bay_config = JarBayShowcase.getConfig( level );
  if ( jar_bay_config !== null )
    return jar_bay_config;

  const stress_config = StressTest.getConfig( level );
  if ( stress_config !== null )
    return stress_config;

  const texture_config = TextureTest.getConfig( level );
  if ( texture_config !== null )
    return texture_config;

  const table_formation_config = TableFormationShowcase.getConfig( level );
  if ( table_formation_config !== null )
    return table_formation_config;

  // A level handed over by the editor wins over the shipped table, so a layout can be
  // playtested before anyone commits it to LEVELS_CONFIG.
  const test_config = TestLevelBridge.getConfig( level );
  if ( test_config !== null )
  {
    if ( !GlobalSettings.IS_PROD )
    {
      validateLayerCount( test_config );
      test_config.layers?.forEach( validateLevelLayer );
      validateTables( test_config );
    }
    return test_config;
  }

  // Server-driven levels (segment-resolved release) override the bundled
  // table for the whole campaign; bundled configs stay as an offline fallback.
  const pack_config = LevelPackService.getConfig( level );
  if ( pack_config !== null )
  {
    if ( !GlobalSettings.IS_PROD )
    {
      validateLayerCount( pack_config );
      pack_config.layers?.forEach( validateLevelLayer );
      validateTables( pack_config );
    }
    return pack_config;
  }

  if ( !areBundledLevelsAllowed() )
  {
    console.error(
      LevelPackService.isActive()
        ? `[LevelConfig] level ${level} is missing from release ${LevelPackService.getReleaseUid() ?? "?"} — refusing to build it from the bundled table`
        : `[LevelConfig] no server release active — refusing to build level ${level} from the bundled table`
    );
    return { level, difficulty: getLevelDifficulty( level ) };
  }

  const config = LEVEL_CONFIG_BY_LEVEL.get( level );
  if ( config === undefined )
  {
    console.warn( `[LevelConfig] No explicit config for level ${level}, defaulting to Common with no layout` );
    return { level, difficulty: getLevelDifficulty( level ) };
  }

  return {
    ...config,
    difficulty: getLevelDifficulty( level ),
  };
}
